import * as license from "../constants/license";
import * as resources from "../constants/resources";
import * as permissions from "../constants/permissions";
import * as methods from "../constants/methods";
import * as modules from "../constants/modules";
import * as rcc from "../constants/rcc";
import * as platforms from "../constants/platforms";
import * as modelHelper from "../helpers/modelHelper";
import * as getterHelper from "../helpers/getterHelper";

/**
 * @class PrivilegeManager
 */
export default class PrivilegeManager {
    /**
     * @param {String}  softwareType
     * @param {String}  platform
     */
    constructor(softwareType, platform = platforms.BROWSER) {
        this._initialSoftwareType = softwareType;
        this._softwareType = softwareType;
        this._isBrowser = platform === platforms.BROWSER;
        this._isWindows = platform === platforms.WINDOWS;
        this._isLinux = platform === platforms.LINUX;
        this._isSettingsMenuVisible = false;
        this._productOne = false;
        this._productPricing = false;
        this._productEntry = false;
        this._productDispensingManual = false;
        this._productDispensing = false;
        this._productPlus = false;
        this._moduleRcc = false;
        this._moduleMatching = false;
        this._modulePackageCheck = false;
        this._modulePartialUpdates = false;
        this._moduleTicketing = false;
        this._tmc = false;
        this._privileges = {};
        this._userGroups = {};
        this._machineMethods = {};
        this._machineDevices = {};
        this._machineConfig = {};
        this._spectroConfig = {};
        this._spectroMethods = {};
        this._disabledDispensing = false;
        this._disabledOrdering = false;
        this._disabledPricing = false;
        this._disabledCompetitiveSearch = false;
        this._disabledTabs = false;
        this._disabledSpectro = false;
        this._disabledServerPlugins = false;
        this._disabledPrint = false;
        this._disabledCustomFormulas = false;
        this._disabledLight = false;
        this._disabledTintometricSystems = false;
        this._disabledQualityControl = false;
        this._disabledBuildingSite = false;
        this._activeComputerLock = false;
        this._activeKiosk = false;

        this.initialize();
    }

    /**
     * @param {String}  softwareType
     */
    set softwareType(softwareType) {
        this._softwareType = softwareType;
    }

    /**
     * @param {Boolean} isBrowser
     */
    set isBrowser(isBrowser) {
        this._isBrowser = isBrowser;
    }

    /**
     * @param {Boolean} isWindows
     */
    set isWindows(isWindows) {
        this._isWindows = isWindows;
    }

    /**
     * @param {Boolean} isLinux
     */
    set isLinux(isLinux) {
        this._isLinux = isLinux;
    }

    /**
     * @param {Boolean} isSettingsMenuVisible
     */
    set isSettingsMenuVisible(isSettingsMenuVisible) {
        this._isSettingsMenuVisible = isSettingsMenuVisible;
    }

    /**
     * @param {Boolean} productOne
     */
    set productOne(productOne) {
        this._productOne = productOne;
    }

    /**
     * @param {Boolean} productPricing
     */
    set productPricing(productPricing) {
        this._productPricing = productPricing;
    }

    /**
     * @param {Boolean} productEntry
     */
    set productEntry(productEntry) {
        this._productEntry = productEntry;
    }

    /**
     * @param {Boolean} productDispensingManual
     */
    set productDispensingManual(productDispensingManual) {
        this._productDispensingManual = productDispensingManual;
    }

    /**
     * @param {Boolean} productDispensing
     */
    set productDispensing(productDispensing) {
        this._productDispensing = productDispensing;
    }

    /**
     * @param {Boolean} productPlus
     */
    set productPlus(productPlus) {
        this._productPlus = productPlus;
    }

    /**
     * @param {Boolean} moduleRcc
     */
    set moduleRcc(moduleRcc) {
        this._moduleRcc = moduleRcc;
    }

    /**
     * @param {Boolean} modulePackageCheck
     */
    set modulePackageCheck(modulePackageCheck) {
        this._modulePackageCheck = modulePackageCheck;
    }

    /**
     * @param {Boolean} moduleMatching
     */
    set moduleMatching(moduleMatching) {
        this._moduleMatching = moduleMatching;
    }

    /**
     * @param {Boolean} modulePartialUpdates
     */
    set modulePartialUpdates(modulePartialUpdates) {
        this._modulePartialUpdates = modulePartialUpdates;
    }

    /**
     * @param {Boolean} moduleTicketing
     */
    set moduleTicketing(moduleTicketing) {
        this._moduleTicketing = moduleTicketing;
    }

    /**
     * @param {Boolean} tmc
     */
    set tmc(tmc) {
        this._tmc = tmc;
    }

    /**
     * @param {Object} privileges
     */
    set privileges(privileges) {
        this._privileges = privileges;
    }

    /**
     * @param {Object} userGroups
     */
    set userGroups(userGroups) {
        this._userGroups = userGroups;
    }

    /**
     * @param {Object} machineMethods
     */
    set machineMethods(machineMethods) {
        this._machineMethods = machineMethods;
    }

    /**
     * @param {Object} machineDevices
     */
    set machineDevices(machineDevices) {
        this._machineDevices = machineDevices;
    }

    /**
     * @param {Object} machineConfig
     */
    set machineConfig(machineConfig) {
        this._machineConfig = machineConfig;
    }

    /**
     * @param {Object} spectroConfig
     */
    set spectroConfig(spectroConfig) {
        this._spectroConfig = spectroConfig;
    }

    /**
     * @param {Object} spectroMethods
     */
    set spectroMethods(spectroMethods) {
        this._spectroMethods = spectroMethods;
    }

    /**
     * @param {Boolean} disabledDispensing
     */
    set disabledDispensing(disabledDispensing) {
        this._disabledDispensing = disabledDispensing;
    }

    /**
     * @param {Boolean} disabledOrdering
     */
    set disabledOrdering(disabledOrdering) {
        this._disabledOrdering = disabledOrdering;
    }

    /**
     * @param {Boolean} disabledPricing
     */
    set disabledPricing(disabledPricing) {
        this._disabledPricing = disabledPricing;
    }

    /**
     * @param {Boolean} disabledCompetitiveSearch
     */
    set disabledCompetitiveSearch(disabledCompetitiveSearch) {
        this._disabledCompetitiveSearch = disabledCompetitiveSearch;
    }

    /**
     * @param {Boolean} disabledTabs
     */
    set disabledTabs(disabledTabs) {
        this._disabledTabs = disabledTabs;
    }

    /**
     * @param {Boolean} disabledSpectro
     */
    set disabledSpectro(disabledSpectro) {
        this._disabledSpectro = disabledSpectro;
    }

    /**
     * @param {Boolean} disabledServerPlugins
     */
    set disabledServerPlugins(disabledServerPlugins) {
        this._disabledServerPlugins = disabledServerPlugins;
    }

    /**
     * @param {Boolean} disabledPrint
     */
    set disabledPrint(disabledPrint) {
        this._disabledPrint = disabledPrint;
    }

    /**
     * @param {Boolean} disabledCustomFormulas
     */
    set disabledCustomFormulas(disabledCustomFormulas) {
        this._disabledCustomFormulas = disabledCustomFormulas;
    }

    /**
     * @param {Boolean} disabledLight
     */
    set disabledLight(disabledLight) {
        this._disabledLight = disabledLight;
    }

    /**
     * @param {Boolean} disabledTintometricSystems
     */
    set disabledTintometricSystems(disabledTintometricSystems) {
        this._disabledTintometricSystems = disabledTintometricSystems;
    }

    /**
     * @param {Boolean} disabledQualityControl
     */
    set disabledQualityControl(disabledQualityControl) {
        this._disabledQualityControl = disabledQualityControl;
    }

    /**
     * @param {Boolean} disabledBuildingSite
     */
    set disabledBuildingSite(disabledBuildingSite) {
        this._disabledBuildingSite = disabledBuildingSite;
    }

    /**
     * @param {Boolean} activeComputerLock
     */
    set activeComputerLock(activeComputerLock) {
        this._activeComputerLock = activeComputerLock;
    }

    /**
     * @param {Boolean} activeKiosk
     */
    set activeKiosk(activeKiosk) {
        this._activeKiosk = activeKiosk;
    }

    /**
     * @return  {PrivilegeManager}
     */
    clone = () => {
        let clone = new PrivilegeManager(this._initialSoftwareType);
        clone.softwareType = this._softwareType;
        clone.isBrowser = this._isBrowser;
        clone.isWindows = this._isWindows;
        clone.isLinux = this._isLinux;
        clone.isSettingsMenuVisible = this._isSettingsMenuVisible;
        clone.productOne = this._productOne;
        clone.productPricing = this._productPricing;
        clone.productEntry = this._productEntry;
        clone.productDispensingManual = this._productDispensingManual;
        clone.productDispensing = this._productDispensing;
        clone.productPlus = this._productPlus;
        clone.moduleRcc = this._moduleRcc;
        clone.moduleMatching = this._moduleMatching;
        clone.modulePackageCheck = this._modulePackageCheck;
        clone.modulePartialUpdates = this._modulePartialUpdates;
        clone.moduleTicketing = this._moduleTicketing;
        clone.tmc = this._tmc;
        clone.privileges = this._privileges;
        clone.userGroups = this._userGroups;
        clone.machineMethods = this._machineMethods;
        clone.machineDevices = this._machineDevices;
        clone.machineConfig = this._machineConfig;
        clone.spectroConfig = this._spectroConfig;
        clone.spectroMethods = this._spectroMethods;
        clone.disabledDispensing = this._disabledDispensing;
        clone.disabledOrdering = this._disabledOrdering;
        clone.disabledPricing = this._disabledPricing;
        clone.disabledCompetitiveSearch = this._disabledCompetitiveSearch;
        clone.disabledTabs = this._disabledTabs;
        clone.disabledSpectro = this._disabledSpectro;
        clone.disabledServerPlugins = this._disabledServerPlugins;
        clone.disabledPrint = this._disabledPrint;
        clone.disabledCustomFormulas = this._disabledCustomFormulas;
        clone.disabledLight = this._disabledLight;
        clone.disabledTintometricSystems = this._disabledTintometricSystems;
        clone.disabledQualityControl = this._disabledQualityControl;
        clone.disabledBuildingSite = this._disabledBuildingSite;
        clone.activeComputerLock = this._activeComputerLock;
        clone.activeKiosk = this._activeKiosk;

        return clone;
    };

    /**
     * Sets default privileges for user before log in.
     */
    initialize = () => {
        this._privileges[resources.FUNCTION_CLOSE_REDLIKE] = permissions.AVAILABLE;
        this._privileges[resources.FUNCTION_OPEN_BROWSER] = permissions.AVAILABLE;

        this.initializeLocalPrivileges();
    };

    /**
     * Sets default privileges that are not defined in server.
     */
    initializeLocalPrivileges = () => {
        this._privileges[resources.FUNCTION_VALIDATE_ORDER_PACKAGE] = permissions.HIDDEN;

        this._privileges = modelHelper.sortObjectKeys(this._privileges);
    };

    /**
     * @param   {String}            softwareType
     * @return  {PrivilegeManager}
     */
    updateSoftwareType = (softwareType) => {
        let clone = this.clone();
        clone.softwareType = softwareType;

        return clone;
    };

    /**
     * @param   {String}            product
     * @param   {Array}             moduleList
     * @param   {Array}             licenseModules
     * @return  {PrivilegeManager}
     */
    updateProduct = (product, moduleList, licenseModules) => {
        let clone = this.clone();

        clone.productOne = product === license.PRODUCT_ONE;
        clone.productPricing = product === license.PRODUCT_ONE || product === license.PRODUCT_PRICING;
        clone.productEntry = product === license.PRODUCT_ENTRY;
        clone.productDispensingManual = product === license.PRODUCT_DISPENSING_MANUAL;
        clone.productDispensing = product === license.PRODUCT_DISPENSING;
        clone.productPlus = product === license.PRODUCT_PLUS;
        clone.moduleRcc = moduleList.some((module) => module === modules.MODULE_RCC);
        clone.modulePackageCheck = licenseModules.some((module) => module.code === modules.MODULE_PACKAGE_CHECK);
        clone.moduleMatching = licenseModules.some(
            (module) => module.code.match(modules.MODULE_MATCHING_REGEX) !== null
        );
        clone.modulePartialUpdates = licenseModules.some((module) => module.code === modules.MODULE_PARTIAL_UPDATES);
        clone.moduleTicketing = licenseModules.some((module) => module.code === modules.MODULE_TICKETING);

        return clone;
    };

    /**
     * @param   {Boolean}           tmc
     * @return  {PrivilegeManager}
     */
    updateTmc = (tmc) => {
        let clone = this.clone();
        clone.tmc = tmc;

        return clone;
    };

    /**
     * @return  {PrivilegeManager}
     */
    resetPrivileges = () => {
        let clone = this.clone();
        clone.privileges = {};
        clone.initialize();

        return clone;
    };

    /**
     * @param   {Object}            privileges
     * @param   {Array}             userGroups
     * @param   {Object}            settingsMenu
     * @return  {PrivilegeManager}
     */
    updateUserPrivileges = (privileges, userGroups, settingsMenu) => {
        if (typeof privileges !== "object" || privileges === null) {
            return this;
        }

        try {
            let settingsMenuPrivileges = getterHelper.getSettingsMenuPrivileges(settingsMenu);

            let privilege;
            for (privilege in settingsMenuPrivileges) {
                privileges[privilege] = settingsMenuPrivileges[privilege];
            }

            let availableGroups = {};

            let group;
            for (group of userGroups) {
                availableGroups[group.name] = true;
            }

            let clone = this.clone();
            clone.privileges = privileges;
            clone.initializeLocalPrivileges();
            clone.isSettingsMenuVisible = settingsMenu.visible;
            clone.userGroups = availableGroups;

            return clone;
        } catch (error) {
            return this;
        }
    };

    /**
     * @param   {Object}            machineMethods
     * @return  {PrivilegeManager}
     */
    updateMachineMethods = (machineMethods) => {
        let clone = this.clone();
        clone.machineMethods = machineMethods;

        return clone;
    };

    /**
     * @param   {Object}            machineDevices
     * @return  {PrivilegeManager}
     */
    updateMachineDevices = (machineDevices) => {
        let clone = this.clone();
        clone.machineDevices = machineDevices;

        return clone;
    };

    /**
     * @param   {Object | null}     machineConfig
     * @return  {PrivilegeManager}
     */
    updateMachineConfig = (machineConfig) => {
        let clone = this.clone();
        clone.machineConfig = machineConfig === null ? {} : machineConfig;

        return clone;
    };

    /**
     * @param   {Object | null}     spectroConfig
     * @return  {PrivilegeManager}
     */
    updateSpectroConfig = (spectroConfig) => {
        let clone = this.clone();
        clone.spectroConfig = spectroConfig === null ? {} : spectroConfig;

        return clone;
    };

    /**
     * @param   {Object}            spectroMethods
     * @return  {PrivilegeManager}
     */
    updateSpectroMethods = (spectroMethods) => {
        let clone = this.clone();
        clone.spectroMethods = spectroMethods;

        return clone;
    };

    /**
     * @return  {PrivilegeManager}
     */
    resetRccDependencies = () => {
        let clone = this.clone();
        clone.machineMethods = {};
        clone.machineDevices = {};
        clone.machineConfig = {};
        clone.spectroConfig = {};
        clone.spectroMethods = {};

        return clone;
    };

    /**
     * @param   {Boolean}           allowedDispensing
     * @param   {Boolean}           allowedOrdering
     * @param   {Boolean}           allowedPricing
     * @param   {Boolean}           allowedCompetitiveSearch
     * @param   {Boolean}           allowedTabs
     * @param   {Boolean}           allowedSpectro
     * @param   {Boolean}           allowedPrint
     * @param   {Boolean}           allowedCustomFormulas
     * @param   {Boolean}           allowedLight
     * @param   {Boolean}           activeComputerLock
     * @param   {Boolean}           activeKiosk
     * @param   {Boolean}           activeQualityControl
     * @return  {PrivilegeManager}
     */
    updateUserSettings = (
        allowedDispensing,
        allowedOrdering,
        allowedPricing,
        allowedCompetitiveSearch,
        allowedTabs,
        allowedSpectro,
        allowedPrint,
        allowedCustomFormulas,
        allowedLight,
        allowedTintometricSystems,
        activeQualityControl,
        allowedBuildingSite,
        activeComputerLock,
        activeKiosk
    ) => {
        let clone = this.clone();
        clone.disabledDispensing = !allowedDispensing;
        clone.disabledOrdering = !allowedOrdering;
        clone.disabledPricing = !allowedPricing;
        clone.disabledCompetitiveSearch = !allowedCompetitiveSearch;
        clone.disabledTabs = !allowedTabs;
        clone.disabledSpectro = !allowedSpectro;
        clone.disabledPrint = !allowedPrint;
        clone.disabledCustomFormulas = !allowedCustomFormulas;
        clone.disabledLight = !allowedLight;
        clone.disabledTintometricSystems = !allowedTintometricSystems;
        clone.disabledQualityControl = !activeQualityControl;
        clone.disabledBuildingSite = !allowedBuildingSite;
        clone.activeComputerLock = activeComputerLock;
        clone.activeKiosk = activeKiosk;

        return clone;
    };

    /**
     * @param   {Boolean}           allowedDispensing
     * @return  {PrivilegeManager}
     */
    updateDisabledDispensing = (allowedDispensing) => {
        let clone = this.clone();
        clone.disabledDispensing = !allowedDispensing;

        return clone;
    };

    /**
     * @param   {Boolean}           allowedOrdering
     * @return  {PrivilegeManager}
     */
    updateDisabledOrdering = (allowedOrdering) => {
        let clone = this.clone();
        clone.disabledOrdering = !allowedOrdering;

        return clone;
    };

    /**
     * @param   {Boolean}           allowedPricing
     * @return  {PrivilegeManager}
     */
    updateDisabledPricing = (allowedPricing) => {
        let clone = this.clone();
        clone.disabledPricing = !allowedPricing;

        return clone;
    };

    /**
     * @param   {Boolean}           allowedCompetitiveSearch
     * @return  {PrivilegeManager}
     */
    updateDisabledCompetitiveSearch = (allowedCompetitiveSearch) => {
        let clone = this.clone();
        clone.disabledCompetitiveSearch = !allowedCompetitiveSearch;

        return clone;
    };

    /**
     * @param   {Boolean}           allowedTabs
     * @return  {PrivilegeManager}
     */
    updateDisabledTabs = (allowedTabs) => {
        let clone = this.clone();
        clone.disabledTabs = !allowedTabs;

        return clone;
    };

    /**
     * @param   {Boolean}           allowedSpectro
     * @return  {PrivilegeManager}
     */
    updateDisabledSpectro = (allowedSpectro) => {
        let clone = this.clone();
        clone.disabledSpectro = !allowedSpectro;

        return clone;
    };

    /**
     * @param   {Boolean}           allowedServerPlugins
     * @return  {PrivilegeManager}
     */
    updateDisabledServerPlugins = (allowedServerPlugins) => {
        let clone = this.clone();
        clone.disabledServerPlugins = !allowedServerPlugins;

        return clone;
    };

    /**
     * @param   {Boolean}           allowedPrint
     * @return  {PrivilegeManager}
     */
    updateDisabledPrint = (allowedPrint) => {
        let clone = this.clone();
        clone.disabledPrint = !allowedPrint;

        return clone;
    };

    /**
     * @param   {Boolean}           allowedCustomFormulas
     * @return  {PrivilegeManager}
     */
    updateDisabledCustomFormulas = (allowedCustomFormulas) => {
        let clone = this.clone();
        clone.disabledCustomFormulas = !allowedCustomFormulas;

        return clone;
    };

    /**
     * @param   {Boolean}           allowedLight
     * @return  {PrivilegeManager}
     */
    updateDisabledLight = (allowedLight) => {
        let clone = this.clone();
        clone.disabledLight = !allowedLight;

        return clone;
    };

    /**
     * @param   {Boolean}           activeQualityControl
     * @return  {PrivilegeManager}
     */
    updateDisabledQualityControl = (activeQualityControl) => {
        let clone = this.clone();
        clone.disabledQualityControl = !activeQualityControl;

        return clone;
    };

    /**
     * @param   {Boolean}           activedBuildingSite
     * @return  {PrivilegeManager}
     */
    updateDisabledBuildingSite = (activedBuildingSite) => {
        let clone = this.clone();
        clone.disabledBuildingSite = !activedBuildingSite;

        return clone;
    };

    /**
     * @param   {Boolean}           activeComputerLock
     * @return  {PrivilegeManager}
     */
    updateComputerLock = (activeComputerLock) => {
        let clone = this.clone();
        clone.activeComputerLock = activeComputerLock;

        return clone;
    };

    /**
     * @param   {Boolean}           activeKiosk
     * @return  {PrivilegeManager}
     */
    updateKiosk = (activeKiosk) => {
        let clone = this.clone();
        clone.activeKiosk = activeKiosk;

        return clone;
    };

    /**
     * Applies license dependencies on user privileges.
     */
    applyProductDependencies = (privileges) => {
        if (this._productPricing) {
            // Components
            privileges[resources.COMPONENT_AUTOTASKS_SETTINGS] = permissions.HIDDEN;
            privileges[resources.COMPONENT_BUILDING_SITE_PANEL] = permissions.HIDDEN;
            privileges[resources.COMPONENT_CANISTER_BARCODES] = permissions.HIDDEN;
            privileges[resources.COMPONENT_CANISTER_BARCODES_MODAL] = permissions.HIDDEN;
            privileges[resources.COMPONENT_CANISTER_FILLING] = permissions.HIDDEN;
            privileges[resources.COMPONENT_CANISTERS_TAB] = permissions.HIDDEN;
            privileges[resources.COMPONENT_COLORANT_PANEL] = permissions.HIDDEN;
            privileges[resources.COMPONENT_COLORANT_REFILL_HISTORY] = permissions.HIDDEN;
            privileges[resources.COMPONENT_DISPENSING_LOGIC] = permissions.HIDDEN;
            privileges[resources.COMPONENT_DRIVER_SETTINGS] = permissions.HIDDEN;
            privileges[resources.COMPONENT_HISTORY_OF_ORDERS_TAB] = permissions.HIDDEN;
            privileges[resources.COMPONENT_MACHINE_DIALOG] = permissions.HIDDEN;
            privileges[resources.COMPONENT_MACHINE_INFO] = permissions.HIDDEN;
            privileges[resources.COMPONENT_MACHINE_REGISTRATION] = permissions.HIDDEN;
            privileges[resources.COMPONENT_MATCHING_TAB] = permissions.HIDDEN;
            privileges[resources.COMPONENT_ORDER_NOTIFICATION_MODAL] = permissions.HIDDEN;
            privileges[resources.COMPONENT_COLORANT_CONSUMPTION] = permissions.HIDDEN;
            privileges[resources.COMPONENT_QUALITY_CONTROL_MODAL] = permissions.HIDDEN;
            privileges[resources.COMPONENT_QUICK_TINT_MODAL] = permissions.HIDDEN;
            privileges[resources.COMPONENT_SMART_PURGE_RULES] = permissions.HIDDEN;
            privileges[resources.COMPONENT_STATISTICS_TAB] = permissions.HIDDEN;
            privileges[resources.COMPONENT_TICKETING_TAB] = permissions.HIDDEN;
            privileges[resources.COMPONENT_TO_DISPENSE_TAB] = permissions.HIDDEN;

            // Menu
            privileges[resources.MENU_MACHINE_AND_DEVICES] = permissions.HIDDEN;
            privileges[resources.MENU_MAINTENANCE] = permissions.HIDDEN;

            // Submenu
            privileges[resources.SUBMENU_COLORANT] = permissions.HIDDEN;
            privileges[resources.SUBMENU_ORDER_SETTINGS] = permissions.HIDDEN;
            privileges[resources.SUBMENU_QUALITY_CONTROL_SETTINGS] = permissions.HIDDEN;

            // Menu content
            privileges[resources.CONTENT_QUALITY_CONTROL] = permissions.HIDDEN;

            // Features
            privileges[resources.FUNCTION_AUTOMATIC_CORRECTION] = permissions.HIDDEN;
            privileges[resources.FUNCTION_DISPENSING] = permissions.HIDDEN;
            privileges[resources.FUNCTION_EXPORT_COLORANT_CONSUMPTION] = permissions.HIDDEN;
            privileges[resources.FUNCTION_MANUAL_CORRECTION] = permissions.HIDDEN;
            privileges[resources.FUNCTION_REFORMULATE] = permissions.HIDDEN;
            privileges[resources.FUNCTION_SAVE_TO_HISTORY] = permissions.HIDDEN;

            // Machine actions
            privileges[resources.MACHINE_ACTION_INITIALIZE] = permissions.HIDDEN;
            privileges[resources.MACHINE_ACTION_RESET] = permissions.HIDDEN;
            privileges[resources.MACHINE_ACTION_HARD_RESET] = permissions.HIDDEN;
            privileges[resources.MACHINE_ACTION_ERROR_RESET] = permissions.HIDDEN;
            privileges[resources.MACHINE_ACTION_SHOW_SERVICE_WINDOW] = permissions.HIDDEN;
            privileges[resources.MACHINE_ACTION_DISCONNECT] = permissions.HIDDEN;
            privileges[resources.MACHINE_ACTION_PURGE] = permissions.HIDDEN;
            privileges[resources.MACHINE_ACTION_PURGE_ALL] = permissions.HIDDEN;
            privileges[resources.MACHINE_ACTION_SMART_PURGE] = permissions.HIDDEN;
            privileges[resources.MACHINE_ACTION_SMART_PURGE_ALL] = permissions.HIDDEN;
            privileges[resources.MACHINE_ACTION_CLEAN_CHANNELS] = permissions.HIDDEN;
            privileges[resources.MACHINE_ACTION_CLEAN_ALL_CHANNELS] = permissions.HIDDEN;
            privileges[resources.MACHINE_ACTION_RECIRCULATE_CHANNELS] = permissions.HIDDEN;
            privileges[resources.MACHINE_ACTION_RECIRCULATE_ALL_CHANNELS] = permissions.HIDDEN;
            privileges[resources.MACHINE_ACTION_STIR_CANISTERS] = permissions.HIDDEN;
            privileges[resources.MACHINE_ACTION_STIR_ALL_CANISTERS] = permissions.HIDDEN;
            privileges[resources.MACHINE_ACTION_CAP_OPEN] = permissions.HIDDEN;
            privileges[resources.MACHINE_ACTION_CAP_CLOSE] = permissions.HIDDEN;
            privileges[resources.MACHINE_ACTION_SHELF_UP] = permissions.HIDDEN;
            privileges[resources.MACHINE_ACTION_SHELF_DOWN] = permissions.HIDDEN;
            privileges[resources.MACHINE_ACTION_PLUG] = permissions.HIDDEN;
            privileges[resources.MACHINE_ACTION_PUNCH] = permissions.HIDDEN;

            // Machine config
            privileges[resources.MACHINE_SETTINGS_CHECK_CAN_BEFORE_DISPENSING] = permissions.HIDDEN;
            privileges[resources.MACHINE_SETTINGS_CHECK_OVERFILL] = permissions.HIDDEN;
            privileges[resources.MACHINE_SETTINGS_DISPENSE_INVALID_COLORANTS] = permissions.HIDDEN;
            privileges[resources.MACHINE_SETTINGS_ENABLE_TMC] = permissions.HIDDEN;
            privileges[resources.MACHINE_SETTINGS_MACHINE_UNIT] = permissions.HIDDEN;
            privileges[resources.MACHINE_SETTINGS_MANUAL_ADDITION] = permissions.HIDDEN;
            privileges[resources.MACHINE_SETTINGS_MINIMAL_DISPENSE_AMOUNT] = permissions.HIDDEN;
            privileges[resources.MACHINE_SETTINGS_PLACE_CAN_SENZOR] = permissions.HIDDEN;
            privileges[resources.MACHINE_SETTINGS_PLACE_CAN_SENZOR_DELAY] = permissions.HIDDEN;
            privileges[resources.MACHINE_SETTINGS_REMOVE_CAN_SENZOR] = permissions.HIDDEN;
            privileges[resources.MACHINE_SETTINGS_REMOVE_CAN_SENZOR_DELAY] = permissions.HIDDEN;
            privileges[resources.MACHINE_SETTINGS_SKIP_PLACE_CAN_DIALOG] = permissions.HIDDEN;
            privileges[resources.MACHINE_SETTINGS_SKIP_REMOVE_CAN_DIALOG] = permissions.HIDDEN;
            privileges[resources.MACHINE_SETTINGS_SPLIT_FORMULA] = permissions.HIDDEN;
            privileges[resources.MACHINE_SETTINGS_VALIDATE_BARCODE_CAN] = permissions.HIDDEN;
            privileges[resources.MACHINE_SETTINGS_VALIDATE_BARCODE_REFILL] = permissions.HIDDEN;
            privileges[resources.MACHINE_SETTINGS_VALIDATE_COLORANTS_DURING_INIT] = permissions.HIDDEN;

            // Settings
            privileges[resources.SETTINGS_AUTOMATIC_PRINT] = permissions.HIDDEN;
            privileges[resources.SETTINGS_BUILDING_SITE] = permissions.HIDDEN;
            privileges[resources.SETTINGS_COLORANT_BATCH_NUMBER_LOGIC] = permissions.HIDDEN;
            privileges[resources.SETTINGS_DISPENSING_ENABLED] = permissions.HIDDEN;
            privileges[resources.SETTINGS_EDIT_COLORANT_ALLOWANCE] = permissions.HIDDEN;
            privileges[resources.SETTINGS_ORDER_MODAL_ENABLED] = permissions.HIDDEN;
            privileges[resources.SETTINGS_PRINT_AFTER_DISPENSING] = permissions.HIDDEN;
            privileges[resources.SETTINGS_PRINT_AS_IMAGE] = permissions.HIDDEN;
            privileges[resources.SETTINGS_PRINT_BEFORE_DISPENSING] = permissions.HIDDEN;
            privileges[resources.SETTINGS_PRODUCT_IMAGE_PREVIEW] = permissions.HIDDEN;
            privileges[resources.SETTINGS_QUALITY_CONTROL_ENABLED] = permissions.HIDDEN;
            privileges[resources.SETTINGS_QUICK_TINT_SORTING_TYPE] = permissions.HIDDEN;
            privileges[resources.SETTINGS_QUICK_TINT_WINDOW_CLOSING_RULES] = permissions.HIDDEN;
            privileges[resources.SETTINGS_ROUNDING] = permissions.HIDDEN;
            privileges[resources.SETTINGS_SOUND_ENABLED] = permissions.HIDDEN;
            privileges[resources.SETTINGS_TABLE_BUILDING_SITE] = permissions.HIDDEN;
            privileges[resources.SETTINGS_UFI_CODE_BASE_VALIDATION] = permissions.HIDDEN;
            privileges[resources.SETTINGS_UFI_CODE_LOGIC] = permissions.HIDDEN;
            privileges[resources.SETTINGS_UFI_CODE_MIN_GRAV_PERCENT] = permissions.HIDDEN;
            privileges[resources.SETTINGS_UFI_CODE_MIN_AMOUNT_GRAV_PERCENT] = permissions.HIDDEN;
        }

        if (this._productEntry) {
            // Components
            privileges[resources.COMPONENT_ACTIVE_SERVER] = permissions.HIDDEN;
            privileges[resources.COMPONENT_AUTOTASKS_SETTINGS] = permissions.HIDDEN;
            privileges[resources.COMPONENT_BARCODE_SCAN_MODAL] = permissions.HIDDEN;
            privileges[resources.COMPONENT_BARCODE_TEST_MODAL] = permissions.HIDDEN;
            privileges[resources.COMPONENT_BUG_REPORT] = permissions.HIDDEN;
            privileges[resources.COMPONENT_BUILDING_SITE_PANEL] = permissions.HIDDEN;
            privileges[resources.COMPONENT_CANISTER_BARCODES] = permissions.HIDDEN;
            privileges[resources.COMPONENT_CANISTER_BARCODES_MODAL] = permissions.HIDDEN;
            privileges[resources.COMPONENT_CLIENT_IDENTIFIER] = permissions.HIDDEN;
            privileges[resources.COMPONENT_CLIENT_SERVER_CONNECTION] = permissions.HIDDEN;
            privileges[resources.COMPONENT_COLOR_SEARCH_TAB] = permissions.HIDDEN;
            privileges[resources.COMPONENT_COLOR_TAB] = permissions.HIDDEN;
            privileges[resources.COMPONENT_COLORANT_CONSUMPTION] = permissions.HIDDEN;
            privileges[resources.COMPONENT_COLORANT_REFILL_HISTORY] = permissions.HIDDEN;
            privileges[resources.COMPONENT_CUSTOMERS_PANEL] = permissions.HIDDEN;
            privileges[resources.COMPONENT_CUSTOMERS_TAB] = permissions.HIDDEN;
            privileges[resources.COMPONENT_EXPORT_STATIC_SQL] = permissions.HIDDEN;
            privileges[resources.COMPONENT_FORMULA_STATUS_PANEL] = permissions.HIDDEN;
            privileges[resources.COMPONENT_HIDE_ITEMS_MODAL] = permissions.HIDDEN;
            privileges[resources.COMPONENT_MATCHING_TAB] = permissions.HIDDEN;
            privileges[resources.COMPONENT_MY_ORDERS_TAB] = permissions.HIDDEN;
            privileges[resources.COMPONENT_POS_INFO] = permissions.HIDDEN;
            privileges[resources.COMPONENT_PRICE_PANEL] = permissions.HIDDEN;
            privileges[resources.COMPONENT_QUALITY_CONTROL_MODAL] = permissions.HIDDEN;
            privileges[resources.COMPONENT_RCC_CONNECTION] = permissions.HIDDEN;
            privileges[resources.COMPONENT_SPECTRO_SETTINGS] = permissions.HIDDEN;
            privileges[resources.COMPONENT_SPECTRO_TAB] = permissions.HIDDEN;
            privileges[resources.COMPONENT_TICKETING_TAB] = permissions.HIDDEN;
            privileges[resources.COMPONENT_TO_DISPENSE_TAB] = permissions.HIDDEN;
            privileges[resources.COMPONENT_UPDATE_OPTIONS] = permissions.HIDDEN;

            // Menu
            privileges[resources.MENU_BUG_REPORT] = permissions.HIDDEN;
            privileges[resources.MENU_DATABASE_CONNECTION] = permissions.HIDDEN;
            privileges[resources.MENU_FILE_TRANSFER] = permissions.HIDDEN;
            privileges[resources.MENU_PRICE_SETTIGNS] = permissions.HIDDEN;
            privileges[resources.MENU_RCC_CONNECTION] = permissions.HIDDEN;
            privileges[resources.MENU_SERVER_CONNECTION] = permissions.HIDDEN;
            privileges[resources.MENU_SIMPLIFIERS] = permissions.HIDDEN;
            privileges[resources.MENU_SPECTRO_SETTINGS] = permissions.HIDDEN;
            privileges[resources.MENU_STATISTICS] = permissions.HIDDEN;
            privileges[resources.MENU_UPDATE] = permissions.HIDDEN;

            // Submenu
            privileges[resources.SUBMENU_AVAILABLE_PRICES] = permissions.HIDDEN;
            privileges[resources.SUBMENU_AUTOTASKS] = permissions.HIDDEN;
            privileges[resources.SUBMENU_COLOR_PANEL] = permissions.HIDDEN;
            privileges[resources.SUBMENU_COLOR_REPRESENTATION] = permissions.HIDDEN;
            privileges[resources.SUBMENU_COLORANT_CONSUMPTION] = permissions.HIDDEN;
            privileges[resources.SUBMENU_COLORANT_PRICING] = permissions.HIDDEN;
            privileges[resources.SUBMENU_DEFAULT_PRICE_VALUES] = permissions.HIDDEN;
            privileges[resources.SUBMENU_HELP_SETTINGS] = permissions.HIDDEN;
            privileges[resources.SUBMENU_KEYBOARD_SHORTCUTS] = permissions.HIDDEN;
            privileges[resources.SUBMENU_LAYOUT] = permissions.HIDDEN;
            privileges[resources.SUBMENU_MARGIN_SETTING] = permissions.HIDDEN;
            privileges[resources.SUBMENU_MATCHING_SETTINGS] = permissions.HIDDEN;
            privileges[resources.SUBMENU_NAVIGATION_ITEMS] = permissions.HIDDEN;
            privileges[resources.SUBMENU_ORDER_SETTINGS] = permissions.HIDDEN;
            privileges[resources.SUBMENU_PACKAGE_PRICING] = permissions.HIDDEN;
            privileges[resources.SUBMENU_POS_INFO] = permissions.HIDDEN;
            privileges[resources.SUBMENU_PRODUCT_PRICING] = permissions.HIDDEN;
            privileges[resources.SUBMENU_PRICING_OPTIONS] = permissions.HIDDEN;
            privileges[resources.SUBMENU_QUALITY_CONTROL_SETTINGS] = permissions.HIDDEN;
            privileges[resources.SUBMENU_REFILL_HISTORY] = permissions.HIDDEN;
            privileges[resources.SUBMENU_REFILL_SETTINGS] = permissions.HIDDEN;
            privileges[resources.SUBMENU_SEARCH_SETTINGS] = permissions.HIDDEN;
            privileges[resources.SUBMENU_TABS] = permissions.HIDDEN;

            // Menu content
            privileges[resources.CONTENT_ADDITIONAL_TAB] = permissions.HIDDEN;
            privileges[resources.CONTENT_AUTOTASKS] = permissions.HIDDEN;
            privileges[resources.CONTENT_BUG_REPORT] = permissions.HIDDEN;
            privileges[resources.CONTENT_COLORANT_CONSUMPTION] = permissions.HIDDEN;
            privileges[resources.CONTENT_COMPETITIVE_SEARCH] = permissions.HIDDEN;
            privileges[resources.CONTENT_CURRENCY_SETTINGS] = permissions.HIDDEN;
            privileges[resources.CONTENT_DATABASE_CONNECTION] = permissions.HIDDEN;
            privileges[resources.CONTENT_ENVIRONMENT] = permissions.HIDDEN;
            privileges[resources.CONTENT_FILE_TRANSFER] = permissions.HIDDEN;
            privileges[resources.CONTENT_GENERAL_SEARCH] = permissions.HIDDEN;
            privileges[resources.CONTENT_IMPORT_AND_EXPORT_PRICE] = permissions.HIDDEN;
            privileges[resources.CONTENT_INITIAL_TAB] = permissions.HIDDEN;
            privileges[resources.CONTENT_MAIN_PRICE_SETTINGS] = permissions.HIDDEN;
            privileges[resources.CONTENT_MATCHING_OPTIONS] = permissions.HIDDEN;
            privileges[resources.CONTENT_MATCHING_RESULT] = permissions.HIDDEN;
            privileges[resources.CONTENT_MATCHING_INFORMATION] = permissions.HIDDEN;
            privileges[resources.CONTENT_MODIFICATION] = permissions.HIDDEN;
            privileges[resources.CONTENT_ORDER_SEARCH] = permissions.HIDDEN;
            privileges[resources.CONTENT_POP_UPS] = permissions.HIDDEN;
            privileges[resources.CONTENT_POS_INFO] = permissions.HIDDEN;
            privileges[resources.CONTENT_PRICE_CALCULATION] = permissions.HIDDEN;
            privileges[resources.CONTENT_QUALITY_CONTROL] = permissions.HIDDEN;
            privileges[resources.CONTENT_RCC_CONNECTION] = permissions.HIDDEN;
            privileges[resources.CONTENT_REFILL_HISTORY] = permissions.HIDDEN;
            privileges[resources.CONTENT_REFILL_SETTINGS] = permissions.HIDDEN;
            privileges[resources.CONTENT_SERVER_CONNECTION] = permissions.HIDDEN;
            privileges[resources.CONTENT_SPECTRO_SETTINGS] = permissions.HIDDEN;
            privileges[resources.CONTENT_UPDATE_OPTIONS] = permissions.HIDDEN;

            // Menu content item
            privileges[resources.CONTENT_ITEM_COLORANT_PRICING] = permissions.HIDDEN;
            privileges[resources.CONTENT_ITEM_CURRENCY_SETTINGS] = permissions.HIDDEN;
            privileges[resources.CONTENT_ITEM_DEFAULT_PRICE_VALUES] = permissions.HIDDEN;
            privileges[resources.CONTENT_ITEM_FILE_TRANSFER_EXPORT] = permissions.HIDDEN;
            privileges[resources.CONTENT_ITEM_FILE_TRANSFER_IMPORT] = permissions.HIDDEN;
            privileges[resources.CONTENT_ITEM_LIGHT_AT_STARTUP] = permissions.HIDDEN;
            privileges[resources.CONTENT_ITEM_MACHINE_BARCODE_REFILL_VALIDATION] = permissions.HIDDEN;
            privileges[resources.CONTENT_ITEM_MACHINE_COLORANT_BATCH_NUMBERS] = permissions.HIDDEN;
            privileges[resources.CONTENT_ITEM_MACHINE_UFI_CODE] = permissions.HIDDEN;
            privileges[resources.CONTENT_ITEM_MARGIN_SETTINGS] = permissions.HIDDEN;
            privileges[resources.CONTENT_ITEM_ONE_TIME_IMPORT] = permissions.HIDDEN;
            privileges[resources.CONTENT_ITEM_PACKAGE_PRICING] = permissions.HIDDEN;
            privileges[resources.CONTENT_ITEM_PRICE_EXPORT] = permissions.HIDDEN;
            privileges[resources.CONTENT_ITEM_PRICE_IMPORT] = permissions.HIDDEN;
            privileges[resources.CONTENT_ITEM_PRODUCT_PRICING] = permissions.HIDDEN;

            // Features
            privileges[resources.FUNCTION_AUTOMATIC_CORRECTION] = permissions.HIDDEN;
            privileges[resources.FUNCTION_CREATE_TEMPLATE] = permissions.HIDDEN;
            privileges[resources.FUNCTION_EDIT_TEMPLATE] = permissions.HIDDEN;
            privileges[resources.FUNCTION_FIND_IN_SETTINGS] = permissions.HIDDEN;
            privileges[resources.FUNCTION_MANUAL_CORRECTION] = permissions.HIDDEN;
            privileges[resources.FUNCTION_SWITCH_TO_LIGHT] = permissions.HIDDEN;

            // Machine config
            privileges[resources.MACHINE_SETTINGS_VALIDATE_BARCODE_REFILL] = permissions.HIDDEN;

            // Settings
            privileges[resources.PRICE_SETTINGS_LOCAL_PRICES] = permissions.HIDDEN;
            privileges[resources.PRICE_SETTINGS_PRICE_DEFINITION] = permissions.HIDDEN;
            privileges[resources.PRICE_SETTINGS_PRICE_GROUP_NUMBER] = permissions.HIDDEN;
            privileges[resources.PRICE_SETTINGS_PRICE_GROUPS] = permissions.HIDDEN;
            privileges[resources.PRICE_SETTINGS_USE_DISCOUNT] = permissions.HIDDEN;
            privileges[resources.PRICE_SETTINGS_USE_MARGIN] = permissions.HIDDEN;
            privileges[resources.PRICE_SETTINGS_USE_MARKUP] = permissions.HIDDEN;
            privileges[resources.SETTINGS_ADDITIONAL_TAB_TYPE] = permissions.HIDDEN;
            privileges[resources.SETTINGS_AUTO_CLOSE_TAB] = permissions.HIDDEN;
            privileges[resources.SETTINGS_AUTOMATIC_CLIENT_START] = permissions.HIDDEN;
            privileges[resources.SETTINGS_AUTOMATIC_LOGOUT] = permissions.HIDDEN;
            privileges[resources.SETTINGS_BARCODES_ENABLED] = permissions.HIDDEN;
            privileges[resources.SETTINGS_BUILDING_SITE] = permissions.HIDDEN;
            privileges[resources.SETTINGS_COLOR_PREVIEW_AS_IMAGE] = permissions.HIDDEN;
            privileges[resources.SETTINGS_COLOR_PREVIEW_RESIZABLE] = permissions.HIDDEN;
            privileges[resources.SETTINGS_COLORANT_BATCH_NUMBER_LOGIC] = permissions.HIDDEN;
            privileges[resources.SETTINGS_COMPETITIVE_COLOR_SEARCH] = permissions.HIDDEN;
            privileges[resources.SETTINGS_CUSTOM_BANNER_POSITION] = permissions.HIDDEN;
            privileges[resources.SETTINGS_CUSTOM_HOME_BUTTON] = permissions.HIDDEN;
            privileges[resources.SETTINGS_CUSTOMERS_ENABLED] = permissions.HIDDEN;
            privileges[resources.SETTINGS_DISPENSING_ENABLED] = permissions.HIDDEN;
            privileges[resources.SETTINGS_FANDECK_GROUPS] = permissions.HIDDEN;
            privileges[resources.SETTINGS_FORMULA_AUTOMATIC_NAMING] = permissions.HIDDEN;
            privileges[resources.SETTINGS_FORMULA_MODIFICATION_PANEL] = permissions.HIDDEN;
            privileges[resources.SETTINGS_FORMULA_PREVIEW] = permissions.HIDDEN;
            privileges[resources.SETTINGS_FORMULA_PROPERTIES_POSITION] = permissions.HIDDEN;
            privileges[resources.SETTINGS_FORMULA_STATUS_PANEL] = permissions.HIDDEN;
            privileges[resources.SETTINGS_FTS] = permissions.HIDDEN;
            privileges[resources.SETTINGS_GENERATE_COLOR_PREVIEW] = permissions.HIDDEN;
            privileges[resources.SETTINGS_HISTORICAL_FORMULAS_POSITION] = permissions.HIDDEN;
            privileges[resources.SETTINGS_IMAGES_SOURCE] = permissions.HIDDEN;
            privileges[resources.SETTINGS_INFO_ITEM_IN_RGB_PREVIEW] = permissions.HIDDEN;
            privileges[resources.SETTINGS_INFO_PANEL_RESIZABLE] = permissions.HIDDEN;
            privileges[resources.SETTINGS_INFO_TYPES] = permissions.HIDDEN;
            privileges[resources.SETTINGS_INIT_TAB_TYPE] = permissions.HIDDEN;
            privileges[resources.SETTINGS_KEYBOARD_SHORTCUTS] = permissions.HIDDEN;
            privileges[resources.SETTINGS_KIOSK] = permissions.HIDDEN;
            privileges[resources.SETTINGS_LIGHT_ENABLED] = permissions.HIDDEN;
            privileges[resources.SETTINGS_LOCK_COMPUTER] = permissions.HIDDEN;
            privileges[resources.SETTINGS_NAVIGATION_ITEMS_ORDERED] = permissions.HIDDEN;
            privileges[resources.SETTINGS_NUMBER_OF_TABS] = permissions.HIDDEN;
            privileges[resources.SETTINGS_ORDER_MODAL_ENABLED] = permissions.HIDDEN;
            privileges[resources.SETTINGS_ORDER_SEARCH_ITEMS] = permissions.HIDDEN;
            privileges[resources.SETTINGS_ORDERING_ENABLED] = permissions.HIDDEN;
            privileges[resources.SETTINGS_OVERWRITE_LAST_TAB] = permissions.HIDDEN;
            privileges[resources.SETTINGS_PRICE_TYPES] = permissions.HIDDEN;
            privileges[resources.SETTINGS_PRICE_VISIBILITY_TYPES] = permissions.HIDDEN;
            privileges[resources.SETTINGS_PRICING_ENABLED] = permissions.HIDDEN;
            privileges[resources.SETTINGS_PRINT_ENABLED] = permissions.HIDDEN;
            privileges[resources.SETTINGS_PRODUCT_FILES_SOURCE] = permissions.HIDDEN;
            privileges[resources.SETTINGS_PRODUCT_GROUPS] = permissions.HIDDEN;
            privileges[resources.SETTINGS_QUALITY_CONTROL_ENABLED] = permissions.HIDDEN;
            privileges[resources.SETTINGS_SCREENSAVER_TIMEOUT] = permissions.HIDDEN;
            privileges[resources.SETTINGS_SEARCH_CHEAPER_COLORS] = permissions.HIDDEN;
            privileges[resources.SETTINGS_SEARCH_PANEL_DROP_DOWN_CALCULATE] = permissions.HIDDEN;
            privileges[resources.SETTINGS_SEARCH_PANEL_ORDER] = permissions.HIDDEN;
            privileges[resources.SETTINGS_SEARCH_PANEL_ORDER_BY_DISTANCE] = permissions.HIDDEN;
            privileges[resources.SETTINGS_SOUND_ENABLED] = permissions.HIDDEN;
            privileges[resources.SETTINGS_SPECTRO_ENABLED] = permissions.HIDDEN;
            privileges[resources.SETTINGS_TABLE_BUILDING_SITE] = permissions.HIDDEN;
            privileges[resources.SETTINGS_TABLE_QUALITY_CONTROL] = permissions.HIDDEN;
            privileges[resources.SETTINGS_TABS_ENABLED] = permissions.HIDDEN;
            privileges[resources.SETTINGS_TABS_MEMORY] = permissions.HIDDEN;
            privileges[resources.SETTINGS_UFI_CODE_BASE_VALIDATION] = permissions.HIDDEN;
            privileges[resources.SETTINGS_UFI_CODE_LOGIC] = permissions.HIDDEN;
            privileges[resources.SETTINGS_UFI_CODE_MIN_GRAV_PERCENT] = permissions.HIDDEN;
            privileges[resources.SETTINGS_UFI_CODE_MIN_AMOUNT_GRAV_PERCENT] = permissions.HIDDEN;
            privileges[resources.SETTINGS_VIRTUAL_KEYBOARD_ENABLED] = permissions.HIDDEN;
            privileges[resources.SETTINGS_ZOOM] = permissions.HIDDEN;
        }
    };

    /**
     * Applies module dependencies on user privileges.
     */
    applyModuleDependencies = (privileges) => {
        if (!this._moduleRcc) {
            privileges[resources.COMPONENT_AUTOTASKS_SETTINGS] = permissions.HIDDEN;
            privileges[resources.COMPONENT_CANISTER_BARCODES] = permissions.HIDDEN;
            privileges[resources.COMPONENT_CANISTER_BARCODES_MODAL] = permissions.HIDDEN;
            privileges[resources.COMPONENT_CANISTER_FILLING] = permissions.HIDDEN;
            privileges[resources.COMPONENT_CANISTERS_TAB] = permissions.HIDDEN;
            privileges[resources.COMPONENT_COLORANT_REFILL_HISTORY] = permissions.HIDDEN;
            privileges[resources.COMPONENT_COLORANT_CONSUMPTION] = permissions.HIDDEN;
            privileges[resources.COMPONENT_DISPENSING_LOGIC] = permissions.HIDDEN;
            privileges[resources.COMPONENT_REFILLING_LOGIC] = permissions.HIDDEN;
            privileges[resources.COMPONENT_DISPENSING_SETTINGS] = permissions.HIDDEN;
            privileges[resources.COMPONENT_DRIVER_SETTINGS] = permissions.HIDDEN;
            privileges[resources.COMPONENT_MACHINE_DIALOG] = permissions.HIDDEN;
            privileges[resources.COMPONENT_MACHINE_INFO] = permissions.HIDDEN;
            privileges[resources.COMPONENT_MACHINE_REGISTRATION] = permissions.HIDDEN;
            privileges[resources.COMPONENT_MACHINE_SETTINGS] = permissions.HIDDEN;
            privileges[resources.COMPONENT_POS_INFO] = permissions.HIDDEN;
            privileges[resources.COMPONENT_QUALITY_CONTROL_MODAL] = permissions.HIDDEN;
            privileges[resources.COMPONENT_QUICK_TINT_MODAL] = permissions.HIDDEN;
            privileges[resources.COMPONENT_RCC_CONNECTION] = permissions.HIDDEN;
            privileges[resources.COMPONENT_SMART_PURGE_RULES] = permissions.HIDDEN;
            privileges[resources.COMPONENT_SPECTRO_DIALOG] = permissions.HIDDEN;
            privileges[resources.COMPONENT_SPECTRO_SETTINGS] = permissions.HIDDEN;
            privileges[resources.COMPONENT_MATCHING_TAB] = permissions.HIDDEN;
            privileges[resources.COMPONENT_SPECTRO_TAB] = permissions.HIDDEN;
            privileges[resources.MENU_MAINTENANCE] = permissions.HIDDEN;
            privileges[resources.MENU_SERVICE_ACTIONS] = permissions.HIDDEN;
            privileges[resources.MENU_STATISTICS] = permissions.HIDDEN;
            privileges[resources.MENU_MACHINE_AND_DEVICES] = permissions.HIDDEN;
            privileges[resources.MENU_DEVICES] = permissions.HIDDEN;
            privileges[resources.MENU_MACHINE_MANAGEMENT] = permissions.HIDDEN;
            privileges[resources.MENU_MACHINE_SETTINGS] = permissions.HIDDEN;
            privileges[resources.MENU_SPECTRO_SETTINGS] = permissions.HIDDEN;
            privileges[resources.MENU_PRINT_SETTINGS] = permissions.HIDDEN;
            privileges[resources.MENU_RCC_CONNECTION] = permissions.HIDDEN;
            privileges[resources.SUBMENU_SINGLE_ACTIONS] = permissions.HIDDEN;
            privileges[resources.SUBMENU_SMART_PURGE_RULES] = permissions.HIDDEN;
            privileges[resources.SUBMENU_AUTOTASKS] = permissions.HIDDEN;
            privileges[resources.SUBMENU_COLORANT_CONSUMPTION] = permissions.HIDDEN;
            privileges[resources.SUBMENU_REFILL_HISTORY] = permissions.HIDDEN;
            privileges[resources.SUBMENU_MACHINE_INFO] = permissions.HIDDEN;
            privileges[resources.SUBMENU_MACHINE_MANAGEMENT] = permissions.HIDDEN;
            privileges[resources.SUBMENU_DISPENSING_SETTINGS] = permissions.HIDDEN;
            privileges[resources.SUBMENU_CANISTER_SETTINGS] = permissions.HIDDEN;
            privileges[resources.SUBMENU_DRIVER_SETTINGS] = permissions.HIDDEN;
            privileges[resources.SUBMENU_POS_INFO] = permissions.HIDDEN;
            privileges[resources.SUBMENU_REFILL_SETTINGS] = permissions.HIDDEN;
            privileges[resources.SUBMENU_QUALITY_CONTROL_SETTINGS] = permissions.HIDDEN;
            privileges[resources.SUBMENU_QUINT_TINT] = permissions.HIDDEN;
            privileges[resources.CONTENT_SINGLE_ACTIONS] = permissions.HIDDEN;
            privileges[resources.CONTENT_SMART_PURGE_RULES] = permissions.HIDDEN;
            privileges[resources.CONTENT_AUTOTASKS] = permissions.HIDDEN;
            privileges[resources.CONTENT_COLORANT_CONSUMPTION] = permissions.HIDDEN;
            privileges[resources.CONTENT_REFILL_HISTORY] = permissions.HIDDEN;
            privileges[resources.CONTENT_MACHINE_INFO] = permissions.HIDDEN;
            privileges[resources.CONTENT_MACHINE_MANAGEMENT] = permissions.HIDDEN;
            privileges[resources.CONTENT_DISPENSING_SETTINGS] = permissions.HIDDEN;
            privileges[resources.CONTENT_CANISTER_SETTINGS] = permissions.HIDDEN;
            privileges[resources.CONTENT_DRIVER_SETTINGS] = permissions.HIDDEN;
            privileges[resources.CONTENT_SPECTRO_SETTINGS] = permissions.HIDDEN;
            privileges[resources.CONTENT_PRINT_SETTINGS] = permissions.HIDDEN;
            privileges[resources.CONTENT_RCC_CONNECTION] = permissions.HIDDEN;
            privileges[resources.CONTENT_POS_INFO] = permissions.HIDDEN;
            privileges[resources.CONTENT_QUALITY_CONTROL] = permissions.HIDDEN;
            privileges[resources.CONTENT_QUICK_TINT_SORTING] = permissions.HIDDEN;
            privileges[resources.CONTENT_QUICK_TINT_CLOSING] = permissions.HIDDEN;
            privileges[resources.CONTENT_ITEM_SINGLE_ACTIONS_CANISTERS] = permissions.HIDDEN;
            privileges[resources.CONTENT_ITEM_SINGLE_ACTIONS_CAP] = permissions.HIDDEN;
            privileges[resources.CONTENT_ITEM_SINGLE_ACTIONS_CLEAN] = permissions.HIDDEN;
            privileges[resources.CONTENT_ITEM_SINGLE_ACTIONS_GENERAL] = permissions.HIDDEN;
            privileges[resources.CONTENT_ITEM_SINGLE_ACTIONS_PLUGGER] = permissions.HIDDEN;
            privileges[resources.CONTENT_ITEM_SINGLE_ACTIONS_PUNCHER] = permissions.HIDDEN;
            privileges[resources.CONTENT_ITEM_SINGLE_ACTIONS_PURGE] = permissions.HIDDEN;
            privileges[resources.CONTENT_ITEM_SINGLE_ACTIONS_RECIRCULATE] = permissions.HIDDEN;
            privileges[resources.CONTENT_ITEM_SINGLE_ACTIONS_SHELF] = permissions.HIDDEN;
            privileges[resources.CONTENT_ITEM_SINGLE_ACTIONS_STIR] = permissions.HIDDEN;
            privileges[resources.CONTENT_ITEM_MACHINE_OPTIONS] = permissions.HIDDEN;
            privileges[resources.CONTENT_ITEM_TMC] = permissions.HIDDEN;
            privileges[resources.CONTENT_ITEM_MACHINE_BARCODE_REFILL_VALIDATION] = permissions.HIDDEN;
            privileges[resources.CONTENT_ITEM_MACHINE_CAN_OPTIONS] = permissions.HIDDEN;
            privileges[resources.CONTENT_ITEM_MACHINE_COLORANT_BATCH_NUMBERS] = permissions.HIDDEN;
            privileges[resources.CONTENT_ITEM_MACHINE_DISPENSING_OPTIONS] = permissions.HIDDEN;
            privileges[resources.CONTENT_ITEM_MACHINE_UFI_CODE] = permissions.HIDDEN;
            privileges[resources.CONTENT_ITEM_PRINTER_SETTINGS] = permissions.HIDDEN;
            privileges[resources.CONTENT_ITEM_TEMPLATE_SETTINGS] = permissions.HIDDEN;
            privileges[resources.CONTENT_ITEM_PRINT_OPTIONS] = permissions.HIDDEN;
            privileges[resources.SETTINGS_SPECTRO_ENABLED] = permissions.HIDDEN;
            privileges[resources.SETTINGS_QUALITY_CONTROL_ENABLED] = permissions.HIDDEN;
            privileges[resources.SETTINGS_QUICK_TINT_SORTING_TYPE] = permissions.HIDDEN;
            privileges[resources.SETTINGS_QUICK_TINT_WINDOW_CLOSING_RULES] = permissions.HIDDEN;
            privileges[resources.FUNCTION_ADD_CANISTER_LEVEL] = permissions.HIDDEN;
            privileges[resources.FUNCTION_AUTOMATIC_CORRECTION] = permissions.HIDDEN;
            privileges[resources.FUNCTION_BACKUP_MACHINE_CONFIGURATION] = permissions.HIDDEN;
            privileges[resources.FUNCTION_DISPENSING] = permissions.HIDDEN;
            privileges[resources.FUNCTION_EXPORT_COLORANT_CONSUMPTION] = permissions.HIDDEN;
            privileges[resources.FUNCTION_FILL_CANISTER_TO_MAX] = permissions.HIDDEN;
            privileges[resources.FUNCTION_MANUAL_CORRECTION] = permissions.HIDDEN;
            privileges[resources.FUNCTION_PRINT] = permissions.HIDDEN;
            privileges[resources.FUNCTION_REFILL_BY_COLORANT_PACKAGE] = permissions.HIDDEN;
            privileges[resources.FUNCTION_REFORMULATE] = permissions.HIDDEN;
            privileges[resources.FUNCTION_SAVE_TO_HISTORY] = permissions.HIDDEN;
            privileges[resources.FUNCTION_SET_CANISTER_BARCODE] = permissions.HIDDEN;
            privileges[resources.FUNCTION_SET_CANISTER_DISPENSE_LOGIC] = permissions.HIDDEN;
            privileges[resources.FUNCTION_SET_CANISTER_LEVEL] = permissions.HIDDEN;
            privileges[resources.FUNCTION_SET_MAX_CANISTER_DISPENSE_AMOUNT] = permissions.HIDDEN;
            privileges[resources.FUNCTION_SPECTRO_CALIBRATE] = permissions.HIDDEN;
            privileges[resources.FUNCTION_SPECTRO_MEASURE] = permissions.HIDDEN;
            privileges[resources.FUNCTION_TRIGGER_REFILL_DIALOG] = permissions.HIDDEN;
            privileges[resources.MACHINE_ACTION_INITIALIZE] = permissions.HIDDEN;
            privileges[resources.MACHINE_ACTION_RESET] = permissions.HIDDEN;
            privileges[resources.MACHINE_ACTION_HARD_RESET] = permissions.HIDDEN;
            privileges[resources.MACHINE_ACTION_ERROR_RESET] = permissions.HIDDEN;
            privileges[resources.MACHINE_ACTION_SHOW_SERVICE_WINDOW] = permissions.HIDDEN;
            privileges[resources.MACHINE_ACTION_DISCONNECT] = permissions.HIDDEN;
            privileges[resources.MACHINE_ACTION_PURGE] = permissions.HIDDEN;
            privileges[resources.MACHINE_ACTION_PURGE_ALL] = permissions.HIDDEN;
            privileges[resources.MACHINE_ACTION_SMART_PURGE] = permissions.HIDDEN;
            privileges[resources.MACHINE_ACTION_SMART_PURGE_ALL] = permissions.HIDDEN;
            privileges[resources.MACHINE_ACTION_CLEAN_CHANNELS] = permissions.HIDDEN;
            privileges[resources.MACHINE_ACTION_CLEAN_ALL_CHANNELS] = permissions.HIDDEN;
            privileges[resources.MACHINE_ACTION_RECIRCULATE_CHANNELS] = permissions.HIDDEN;
            privileges[resources.MACHINE_ACTION_RECIRCULATE_ALL_CHANNELS] = permissions.HIDDEN;
            privileges[resources.MACHINE_ACTION_STIR_CANISTERS] = permissions.HIDDEN;
            privileges[resources.MACHINE_ACTION_STIR_ALL_CANISTERS] = permissions.HIDDEN;
            privileges[resources.MACHINE_ACTION_CAP_OPEN] = permissions.HIDDEN;
            privileges[resources.MACHINE_ACTION_CAP_CLOSE] = permissions.HIDDEN;
            privileges[resources.MACHINE_ACTION_SHELF_UP] = permissions.HIDDEN;
            privileges[resources.MACHINE_ACTION_SHELF_DOWN] = permissions.HIDDEN;
            privileges[resources.MACHINE_ACTION_PLUG] = permissions.HIDDEN;
            privileges[resources.MACHINE_ACTION_PUNCH] = permissions.HIDDEN;
        }

        if (this._modulePackageCheck) {
            privileges[resources.FUNCTION_VALIDATE_ORDER_PACKAGE] = permissions.AVAILABLE;
        }

        if (!this._moduleMatching) {
            privileges[resources.COMPONENT_MATCHING_TAB] = permissions.HIDDEN;
            privileges[resources.SUBMENU_MATCHING_SETTINGS] = permissions.HIDDEN;
            privileges[resources.CONTENT_MATCHING_OPTIONS] = permissions.HIDDEN;
            privileges[resources.CONTENT_MATCHING_RESULT] = permissions.HIDDEN;
            privileges[resources.CONTENT_MATCHING_INFORMATION] = permissions.HIDDEN;
            privileges[resources.SETTINGS_MATCHING_ROW_COUNT] = permissions.HIDDEN;
            privileges[resources.FUNCTION_AUTOMATIC_CORRECTION] = permissions.HIDDEN;
            privileges[resources.FUNCTION_REFORMULATE] = permissions.HIDDEN;
        }

        if (!this._modulePartialUpdates) {
            privileges[resources.COMPONENT_PARTIAL_UPDATES_MODAL] = permissions.HIDDEN;
        }

        if (!this._moduleTicketing) {
            privileges[resources.COMPONENT_TICKETING_TAB] = permissions.HIDDEN;
            privileges[resources.COMPONENT_SERVICE_TICKET_MODAL] = permissions.HIDDEN;
        }

        if (!this._tmc) {
            privileges[resources.COMPONENT_SERVICE_TICKET_MODAL] = permissions.HIDDEN;
        }
    };

    /**
     * Applies initial software type dependencies on user privileges.
     */
    applyInitialSoftwareTypeDependencies = (privileges) => {
        if (this._initialSoftwareType !== modules.SOFTWARE_GAMMA) {
            privileges[resources.FUNCTION_SWITCH_TO_INTERGAMMA] = permissions.HIDDEN;
        }
    };

    /**
     * Applies software type dependencies on user privileges.
     */
    applySoftwareTypeDependencies = (privileges) => {
        if (this._softwareType !== modules.SOFTWARE_GAMMA) {
            privileges[resources.COMPONENT_COLORANT_STOCK_MODAL] = permissions.HIDDEN;
        }

        if (this._softwareType === modules.SOFTWARE_COLORIS) {
            privileges[resources.SETTINGS_CUSTOM_BANNER_POSITION] = permissions.HIDDEN;
            privileges[resources.SETTINGS_PRICE_VISIBILITY_TYPES] = permissions.HIDDEN;
            privileges[resources.SETTINGS_COLOR_PREVIEW_RESIZABLE] = permissions.HIDDEN;
            privileges[resources.SETTINGS_COLORANT_PANEL_LIMIT] = permissions.HIDDEN;
            privileges[resources.SETTINGS_INFO_PANEL_RESIZABLE] = permissions.HIDDEN;
            privileges[resources.SUBMENU_NAVIGATION_ITEMS] = permissions.HIDDEN;
            privileges[resources.SETTINGS_NAVIGATION_ITEMS_ORDERED] = permissions.HIDDEN;
            privileges[resources.SETTINGS_BLEND_FORMULA_AND_CODE] = permissions.HIDDEN;
            privileges[resources.SETTINGS_FORMULA_STATUS_PANEL] = permissions.HIDDEN;
            privileges[resources.FUNCTION_RESET_FORMULA_TAB] = permissions.AVAILABLE;
        }

        if (this._softwareType === modules.SOFTWARE_LIGHT) {
            privileges[resources.COMPONENT_REGISTRATION] = permissions.HIDDEN;
            privileges[resources.SETTINGS_TABS_MEMORY] = permissions.HIDDEN;
        }
    };

    /**
     * Applies browser dependencies on user privileges.
     */
    applyPlatformDependencies = (privileges) => {
        if (this._isBrowser) {
            privileges[resources.FUNCTION_EXPORT_COLORANT_CONSUMPTION] = permissions.HIDDEN;
            privileges[resources.FUNCTION_EXPORT_PRINT_LABEL_TO_IMG] = permissions.HIDDEN;
            privileges[resources.FUNCTION_EXPORT_PRINT_LABEL_TO_PDF] = permissions.HIDDEN;
            privileges[resources.FUNCTION_EXPORT_PRODUCT_FILE_TO_PDF] = permissions.HIDDEN;
            privileges[resources.FUNCTION_REBOOT_PC] = permissions.HIDDEN;
            privileges[resources.FUNCTION_SHUTDOWN_PC] = permissions.HIDDEN;
            privileges[resources.FUNCTION_BACKUP_MACHINE_CONFIGURATION] = permissions.HIDDEN;
            privileges[resources.SETTINGS_LOCK_COMPUTER] = permissions.HIDDEN;
            privileges[resources.SETTINGS_TABS_MEMORY] = permissions.HIDDEN;
            privileges[resources.SETTINGS_DISK_SPACE_NOTIFICATION] = permissions.HIDDEN;
            privileges[resources.CONTENT_ITEM_LIGHT_AT_STARTUP] = permissions.HIDDEN;
            privileges[resources.CONTENT_IMPORT_AND_EXPORT_PRICE] = permissions.HIDDEN;
            privileges[resources.COMPONENT_IMPORT_DATA] = permissions.HIDDEN;
            privileges[resources.COMPONENT_IMPORT_SETTINGS] = permissions.HIDDEN;
            privileges[resources.COMPONENT_EXPORT_LOCAL_DB] = permissions.HIDDEN;
            privileges[resources.COMPONENT_EXPORT_SETTINGS] = permissions.HIDDEN;
            privileges[resources.COMPONENT_EXPORT_STATIC_SQL] = permissions.HIDDEN;
            privileges[resources.CONTENT_ITEM_CUSTOM_DATA] = permissions.HIDDEN;
            privileges[resources.CONTENT_ITEM_LOCAL_DATA] = permissions.HIDDEN;
            privileges[resources.CONTENT_IMPORT_CUSTOM_DATA] = permissions.HIDDEN;
            privileges[resources.MENU_IMPORT_CUSTOM_DATA] = permissions.HIDDEN;
        }

        if (this._isLinux) {
            privileges[resources.SETTINGS_LOCK_COMPUTER] = permissions.HIDDEN;
        }

        if (this._isWindows) {
            privileges[resources.SETTINGS_PRINT_AS_IMAGE] = permissions.HIDDEN;
        }
    };

    /**
     * Applies machine dependencies on user privileges.
     */
    applyMachineDependencies = (privileges) => {
        if (this._machineMethods[methods.METHOD_MACHINE_START_DISPENSE_FORMULA] === undefined) {
            privileges[resources.FUNCTION_DISPENSING] = permissions.HIDDEN;
        }

        if (this._machineMethods[methods.METHOD_MACHINE_START_INITIALIZE] === undefined) {
            privileges[resources.MACHINE_ACTION_INITIALIZE] = permissions.HIDDEN;
        }

        if (this._machineMethods[methods.METHOD_MACHINE_START_RESET] === undefined) {
            privileges[resources.MACHINE_ACTION_RESET] = permissions.HIDDEN;
        }

        if (this._machineMethods[methods.METHOD_MACHINE_START_HARD_RESET] === undefined) {
            privileges[resources.MACHINE_ACTION_HARD_RESET] = permissions.HIDDEN;
        }

        if (this._machineMethods[methods.METHOD_MACHINE_START_ERRORS_RESET] === undefined) {
            privileges[resources.MACHINE_ACTION_ERROR_RESET] = permissions.HIDDEN;
        }

        if (this._machineMethods[methods.METHOD_MACHINE_SHOW_SERVICE_WINDOW] === undefined) {
            privileges[resources.MACHINE_ACTION_DISCONNECT] = permissions.HIDDEN;
        }

        if (this._machineMethods[methods.METHOD_MACHINE_START_DISCONNECT] === undefined) {
            privileges[resources.MACHINE_ACTION_SHOW_SERVICE_WINDOW] = permissions.HIDDEN;
        }

        if (this._machineMethods[methods.METHOD_MACHINE_START_PURGE] === undefined) {
            privileges[resources.MACHINE_ACTION_PURGE] = permissions.HIDDEN;
        }

        if (this._machineMethods[methods.METHOD_MACHINE_START_PURGE_ALL] === undefined) {
            privileges[resources.MACHINE_ACTION_PURGE_ALL] = permissions.HIDDEN;
        }

        if (this._machineMethods[methods.METHOD_MACHINE_START_SMART_PURGE] === undefined) {
            privileges[resources.MACHINE_ACTION_SMART_PURGE] = permissions.HIDDEN;
        }

        if (this._machineMethods[methods.METHOD_MACHINE_START_SMART_PURGE_ALL] === undefined) {
            privileges[resources.MACHINE_ACTION_SMART_PURGE_ALL] = permissions.HIDDEN;
        }

        if (this._machineMethods[methods.METHOD_MACHINE_START_CLEAN_CHANNELS] === undefined) {
            privileges[resources.MACHINE_ACTION_CLEAN_CHANNELS] = permissions.HIDDEN;
        }

        if (this._machineMethods[methods.METHOD_MACHINE_START_CLEAN_ALL] === undefined) {
            privileges[resources.MACHINE_ACTION_CLEAN_ALL_CHANNELS] = permissions.HIDDEN;
        }

        if (this._machineMethods[methods.METHOD_MACHINE_START_RECIRCULATE_CHANNELS] === undefined) {
            privileges[resources.MACHINE_ACTION_RECIRCULATE_CHANNELS] = permissions.HIDDEN;
        }

        if (this._machineMethods[methods.METHOD_MACHINE_START_RECIRCULATE_ALL] === undefined) {
            privileges[resources.MACHINE_ACTION_RECIRCULATE_ALL_CHANNELS] = permissions.HIDDEN;
        }

        if (this._machineMethods[methods.METHOD_MACHINE_START_STIR_CANISTERS] === undefined) {
            privileges[resources.MACHINE_ACTION_STIR_CANISTERS] = permissions.HIDDEN;
        }

        if (this._machineMethods[methods.METHOD_MACHINE_START_STIR_ALL] === undefined) {
            privileges[resources.MACHINE_ACTION_STIR_ALL_CANISTERS] = permissions.HIDDEN;
        }

        if (this._machineMethods[methods.METHOD_MACHINE_START_SHELF] === undefined) {
            privileges[resources.MACHINE_ACTION_SHELF_UP] = permissions.HIDDEN;
            privileges[resources.MACHINE_ACTION_SHELF_DOWN] = permissions.HIDDEN;
        }

        if (this._machineMethods[methods.METHOD_MACHINE_START_CAP] === undefined) {
            privileges[resources.MACHINE_ACTION_CAP_OPEN] = permissions.HIDDEN;
            privileges[resources.MACHINE_ACTION_CAP_CLOSE] = permissions.HIDDEN;
        }

        if (this._machineMethods[methods.METHOD_MACHINE_START_PUNCH] === undefined) {
            privileges[resources.MACHINE_ACTION_PUNCH] = permissions.HIDDEN;
        }

        if (this._machineMethods[methods.METHOD_MACHINE_START_PLUG] === undefined) {
            privileges[resources.MACHINE_ACTION_PLUG] = permissions.HIDDEN;
        }

        if (this._machineMethods[methods.METHOD_MACHINE_CANISTERS] === undefined) {
            privileges[resources.COMPONENT_CANISTERS_TAB] = permissions.HIDDEN;
            privileges[resources.COMPONENT_CANISTER_BARCODES] = permissions.HIDDEN;
            privileges[resources.COMPONENT_CANISTER_BARCODES_MODAL] = permissions.HIDDEN;
            privileges[resources.COMPONENT_CANISTER_FILLING] = permissions.HIDDEN;
            privileges[resources.COMPONENT_COLORANT_REFILL_HISTORY] = permissions.HIDDEN;
            privileges[resources.COMPONENT_DISPENSING_LOGIC] = permissions.HIDDEN;
            privileges[resources.COMPONENT_REFILLING_LOGIC] = permissions.HIDDEN;
        }

        if (this._machineMethods[methods.METHOD_MACHINE_SET_CANISTER_LEVEL] === undefined) {
            privileges[resources.FUNCTION_SET_CANISTER_LEVEL] = permissions.HIDDEN;
            privileges[resources.FUNCTION_FILL_CANISTER_TO_MAX] = permissions.HIDDEN;
        }

        if (this._machineMethods[methods.METHOD_MACHINE_ADD_CANISTER_LEVEL] === undefined) {
            privileges[resources.FUNCTION_ADD_CANISTER_LEVEL] = permissions.HIDDEN;
            privileges[resources.FUNCTION_REFILL_BY_COLORANT_PACKAGE] = permissions.HIDDEN;
            privileges[resources.FUNCTION_TRIGGER_REFILL_DIALOG] = permissions.HIDDEN;
        }

        if (this._machineMethods[methods.METHOD_MACHINE_SET_MAX_DISPENSE_LEVEL] === undefined) {
            privileges[resources.FUNCTION_SET_MAX_CANISTER_DISPENSE_AMOUNT] = permissions.HIDDEN;
        }

        if (this._machineMethods[methods.METHOD_MACHINE_SET_CANISTER_UFI_CODE] === undefined) {
            privileges[resources.FUNCTION_SET_CANISTER_UFI_CODE] = permissions.HIDDEN;
        }

        if (this._machineMethods[methods.METHOD_MACHINE_SET_CANISTER_THRESHOLD_PERCENT] === undefined) {
            privileges[resources.COMPONENT_REFILLING_LOGIC] = permissions.HIDDEN;
        }

        if (
            this._machineMethods[methods.METHOD_MACHINE_PURGE_RULE_ADD] === undefined ||
            this._machineMethods[methods.METHOD_MACHINE_PURGE_RULE_UPDATE] === undefined
        ) {
            if (privileges[resources.COMPONENT_SMART_PURGE_RULES] !== permissions.HIDDEN) {
                privileges[resources.COMPONENT_SMART_PURGE_RULES] = permissions.NOT_AVAILABLE;
            }
        }

        if (this._machineMethods[methods.METHOD_MACHINE_PURGE_RULES_LIST] === undefined) {
            privileges[resources.COMPONENT_SMART_PURGE_RULES] = permissions.HIDDEN;
        }

        if (
            this._machineMethods[methods.METHOD_MACHINE_AUTOTASK_ADD_GENERIC] === undefined ||
            this._machineMethods[methods.METHOD_MACHINE_AUTOTASK_UPDATE_GENERIC] === undefined ||
            this._machineMethods[methods.METHOD_MACHINE_AUTOTASK_DELETE] === undefined
        ) {
            if (privileges[resources.COMPONENT_AUTOTASKS_SETTINGS] !== permissions.HIDDEN) {
                privileges[resources.COMPONENT_AUTOTASKS_SETTINGS] = permissions.NOT_AVAILABLE;
            }
        }

        if (this._machineMethods[methods.METHOD_MACHINE_GET_AUTOTASKS] === undefined) {
            privileges[resources.COMPONENT_AUTOTASKS_SETTINGS] = permissions.HIDDEN;
        }

        if (this._machineMethods[methods.METHOD_MACHINE_GET_PURGE_STATS] === undefined) {
            privileges[resources.COMPONENT_COLORANT_CONSUMPTION] = permissions.HIDDEN;
            privileges[resources.FUNCTION_EXPORT_COLORANT_CONSUMPTION] = permissions.HIDDEN;
        }

        if (this._machineMethods[methods.METHOD_MACHINE_HISTORY_REFILLS] === undefined) {
            privileges[resources.COMPONENT_COLORANT_REFILL_HISTORY] = permissions.HIDDEN;
        }

        if (this._machineMethods[methods.METHOD_MACHINE_INFO] === undefined) {
            privileges[resources.COMPONENT_MACHINE_INFO] = permissions.HIDDEN;
        }

        if (this._machineMethods[methods.METHOD_MACHINE_BACKUP_CONFIGURATION] === undefined) {
            privileges[resources.FUNCTION_BACKUP_MACHINE_CONFIGURATION] = permissions.HIDDEN;
        }

        if (this._machineMethods[methods.METHOD_MACHINE_SET_CANISTER_BARCODE] === undefined) {
            privileges[resources.FUNCTION_SET_CANISTER_BARCODE] = permissions.HIDDEN;
        }

        if (this._machineMethods[methods.METHOD_MACHINE_SET_CANISTER_DISPENSE_LOGIC_TYPE] === undefined) {
            privileges[resources.FUNCTION_SET_CANISTER_DISPENSE_LOGIC] = permissions.HIDDEN;
        }

        if (this._machineMethods[methods.METHOD_MACHINE_SET_MAX_DISPENSE_LEVEL] === undefined) {
            privileges[resources.FUNCTION_SET_MAX_CANISTER_DISPENSE_AMOUNT] = permissions.HIDDEN;
        }
    };

    /**
     * Applies machine devices dependencies on user privileges.
     */
    applyMachineDevicesDependencies = (privileges) => {
        if (this._machineDevices[rcc.MACHINE_DEVICE_SHELF] !== true) {
            privileges[resources.MACHINE_ACTION_SHELF_UP] = permissions.HIDDEN;
            privileges[resources.MACHINE_ACTION_SHELF_DOWN] = permissions.HIDDEN;
        }

        if (this._machineDevices[rcc.MACHINE_DEVICE_CAP] !== true) {
            privileges[resources.MACHINE_ACTION_CAP_OPEN] = permissions.HIDDEN;
            privileges[resources.MACHINE_ACTION_CAP_CLOSE] = permissions.HIDDEN;
        }

        if (this._machineDevices[rcc.MACHINE_DEVICE_PUNCHER] !== true) {
            privileges[resources.MACHINE_ACTION_PUNCH] = permissions.HIDDEN;
        }

        if (this._machineDevices[rcc.MACHINE_DEVICE_PLUGGER] !== true) {
            privileges[resources.MACHINE_ACTION_PLUG] = permissions.HIDDEN;
        }
    };

    /**
     * Applies spectro dependencies on user privileges.
     */
    applySpectroDependencies = (privileges) => {
        if (this._spectroConfig.type === undefined || this._spectroConfig.type === rcc.SPECTRO_TYPE_NONE) {
            privileges[resources.COMPONENT_SPECTRO_DIALOG] = permissions.HIDDEN;
        }

        if (this._spectroMethods[methods.METHOD_SPECTRO_START_CALIBRATE] === undefined) {
            privileges[resources.FUNCTION_SPECTRO_CALIBRATE] = permissions.HIDDEN;
        }

        if (this._spectroMethods[methods.METHOD_SPECTRO_START_MEASURE] === undefined) {
            privileges[resources.FUNCTION_SPECTRO_MEASURE] = permissions.HIDDEN;
        }
    };

    /**
     * Applies custom dependencies on user privileges.
     */
    applyCustomDependencies = (privileges) => {
        if (this._disabledDispensing) {
            privileges[resources.FUNCTION_DISPENSING] = permissions.HIDDEN;
            privileges[resources.FUNCTION_SAVE_TO_HISTORY] = permissions.HIDDEN;
            privileges[resources.CONTENT_ITEM_MACHINE_BARCODE_REFILL_VALIDATION] = permissions.HIDDEN;
            privileges[resources.CONTENT_ITEM_MACHINE_CAN_OPTIONS] = permissions.HIDDEN;
            privileges[resources.CONTENT_ITEM_MACHINE_COLORANT_BATCH_NUMBERS] = permissions.HIDDEN;
            privileges[resources.CONTENT_ITEM_MACHINE_DISPENSING_OPTIONS] = permissions.HIDDEN;
            privileges[resources.CONTENT_ITEM_MACHINE_UFI_CODE] = permissions.HIDDEN;
            privileges[resources.CONTENT_DISPENSING_SETTINGS] = permissions.HIDDEN;
            privileges[resources.SUBMENU_DISPENSING_SETTINGS] = permissions.HIDDEN;
            privileges[resources.CONTENT_REFILL_SETTINGS] = permissions.HIDDEN;
            privileges[resources.SUBMENU_REFILL_SETTINGS] = permissions.HIDDEN;
        }

        if (this._disabledOrdering) {
            privileges[resources.FUNCTION_ORDER] = permissions.HIDDEN;
            privileges[resources.SETTINGS_ORDER_MODAL_ENABLED] = permissions.HIDDEN;
            privileges[resources.SETTINGS_SOUND_ENABLED] = permissions.HIDDEN;
            privileges[resources.SUBMENU_ORDER_SETTINGS] = permissions.HIDDEN;
        }

        if (this._disabledPricing) {
            privileges[resources.COMPONENT_PRICE_PANEL] = permissions.HIDDEN;
            privileges[resources.SETTINGS_PRICE_TYPES] = permissions.HIDDEN;
            privileges[resources.SETTINGS_PRICE_VISIBILITY_TYPES] = permissions.HIDDEN;
            privileges[resources.PRICE_SETTINGS_LOCAL_PRICES] = permissions.HIDDEN;
            privileges[resources.PRICE_SETTINGS_USE_MARGIN] = permissions.HIDDEN;
            privileges[resources.PRICE_SETTINGS_USE_MARKUP] = permissions.HIDDEN;
            privileges[resources.PRICE_SETTINGS_USE_DISCOUNT] = permissions.HIDDEN;
            privileges[resources.CONTENT_ITEM_DEFAULT_PRICE_VALUES] = permissions.HIDDEN;
            privileges[resources.CONTENT_ITEM_COLORANT_PRICING] = permissions.HIDDEN;
            privileges[resources.CONTENT_ITEM_MARGIN_SETTINGS] = permissions.HIDDEN;
            privileges[resources.CONTENT_ITEM_CURRENCY_SETTINGS] = permissions.HIDDEN;
            privileges[resources.CONTENT_ITEM_PACKAGE_PRICING] = permissions.HIDDEN;
            privileges[resources.CONTENT_ITEM_PRODUCT_PRICING] = permissions.HIDDEN;
            privileges[resources.CONTENT_ITEM_PRICE_IMPORT] = permissions.HIDDEN;
            privileges[resources.CONTENT_ITEM_PRICE_EXPORT] = permissions.HIDDEN;
            privileges[resources.CONTENT_MAIN_PRICE_SETTINGS] = permissions.HIDDEN;
            privileges[resources.CONTENT_PRICE_CALCULATION] = permissions.HIDDEN;
            privileges[resources.CONTENT_IMPORT_AND_EXPORT_PRICE] = permissions.HIDDEN;
            privileges[resources.CONTENT_CURRENCY_SETTINGS] = permissions.HIDDEN;
            privileges[resources.SUBMENU_AVAILABLE_PRICES] = permissions.HIDDEN;
            privileges[resources.SUBMENU_PRICING_OPTIONS] = permissions.HIDDEN;
            privileges[resources.SUBMENU_DEFAULT_PRICE_VALUES] = permissions.HIDDEN;
            privileges[resources.SUBMENU_MARGIN_SETTING] = permissions.HIDDEN;
            privileges[resources.SUBMENU_COLORANT_PRICING] = permissions.HIDDEN;
            privileges[resources.SUBMENU_PRODUCT_PRICING] = permissions.HIDDEN;
            privileges[resources.SUBMENU_PACKAGE_PRICING] = permissions.HIDDEN;
            privileges[resources.MENU_PRICE_SETTIGNS] = permissions.HIDDEN;
        }

        if (this._disabledCompetitiveSearch) {
            privileges[resources.SETTINGS_MAX_TOLERANCE_FOR_COMPETITIVE_COLORS] = permissions.HIDDEN;
            privileges[resources.SETTINGS_NUMBER_OF_COMPETITIVE_COLORS] = permissions.HIDDEN;
            privileges[resources.SUBMENU_COLOR_PANEL] = permissions.HIDDEN;
            privileges[resources.CONTENT_COMPETITIVE_SEARCH] = permissions.HIDDEN;
            privileges[resources.FUNCTION_COMPETITIVE_COLOR_SEARCH] = permissions.HIDDEN;
        }

        if (this._disabledTabs) {
            privileges[resources.COMPONENT_TAB_BAR_PANEL] = permissions.HIDDEN;
            privileges[resources.CONTENT_ADDITIONAL_TAB] = permissions.HIDDEN;
            privileges[resources.SETTINGS_ADDITIONAL_TAB_TYPE] = permissions.HIDDEN;
            privileges[resources.SETTINGS_NUMBER_OF_TABS] = permissions.HIDDEN;
            privileges[resources.SETTINGS_OVERWRITE_LAST_TAB] = permissions.HIDDEN;
            privileges[resources.FUNCTION_RESET_FORMULA_TAB] = permissions.AVAILABLE;
        }

        if (this._disabledSpectro) {
            privileges[resources.COMPONENT_SPECTRO_TAB] = permissions.HIDDEN;
            privileges[resources.COMPONENT_SPECTRO_DIALOG] = permissions.HIDDEN;
            privileges[resources.COMPONENT_SPECTRO_SETTINGS] = permissions.HIDDEN;
            privileges[resources.CONTENT_SPECTRO_SETTINGS] = permissions.HIDDEN;
            privileges[resources.MENU_SPECTRO_SETTINGS] = permissions.HIDDEN;
            privileges[resources.FUNCTION_SPECTRO_CALIBRATE] = permissions.HIDDEN;
            privileges[resources.FUNCTION_SPECTRO_MEASURE] = permissions.HIDDEN;
        }

        if (this._disabledServerPlugins) {
            privileges[resources.COMPONENT_SERVER_PLUGINS] = permissions.HIDDEN;
        }

        if (this._disabledPrint) {
            privileges[resources.SETTINGS_PRINTER] = permissions.HIDDEN;
            privileges[resources.SETTINGS_PRINT_AS_IMAGE] = permissions.HIDDEN;
            privileges[resources.SETTINGS_PRINTER_TEMPLATE] = permissions.HIDDEN;
            privileges[resources.SETTINGS_PRINTER_TEMPLATE_QUICKTINT] = permissions.HIDDEN;
            privileges[resources.SETTINGS_PRINT_ON_REQUEST] = permissions.HIDDEN;
            privileges[resources.SETTINGS_AUTOMATIC_PRINT] = permissions.HIDDEN;
            privileges[resources.SETTINGS_PRINT_BEFORE_DISPENSING] = permissions.HIDDEN;
            privileges[resources.SETTINGS_PRINT_AFTER_DISPENSING] = permissions.HIDDEN;
            privileges[resources.CONTENT_ITEM_PRINTER_SETTINGS] = permissions.HIDDEN;
            privileges[resources.CONTENT_ITEM_TEMPLATE_SETTINGS] = permissions.HIDDEN;
            privileges[resources.CONTENT_ITEM_PRINT_OPTIONS] = permissions.HIDDEN;
            privileges[resources.CONTENT_PRINT_SETTINGS] = permissions.HIDDEN;
            privileges[resources.MENU_PRINT_SETTINGS] = permissions.HIDDEN;
            privileges[resources.FUNCTION_PRINT] = permissions.HIDDEN;
        }

        if (this._disabledCustomFormulas) {
            privileges[resources.COMPONENT_CUSTOM_FORMULA_TAB] = permissions.HIDDEN;
            privileges[resources.FUNCTION_SEARCH_CUSTOM_FORMULAS] = permissions.HIDDEN;
        }

        if (this._disabledLight) {
            privileges[resources.SETTINGS_CUSTOM_HOME_BUTTON] = permissions.HIDDEN;
            privileges[resources.SETTINGS_VIRTUAL_KEYBOARD_ENABLED] = permissions.HIDDEN;
            privileges[resources.CONTENT_ITEM_LIGHT_AT_STARTUP] = permissions.HIDDEN;
            privileges[resources.FUNCTION_SWITCH_TO_LIGHT] = permissions.HIDDEN;
        }

        if (this._disabledTintometricSystems) {
            privileges[resources.COMPONENT_ALTERNATIVE_FORMULAS] = permissions.HIDDEN;
            privileges[resources.COMPONENT_MACHINE_OPTIONS] = permissions.HIDDEN;
            privileges[resources.SETTINGS_TINTOMETRIC_SYSTEM] = permissions.HIDDEN;
            privileges[resources.SETTINGS_ALTERNATIVE_FORMULAS_POSITION] = permissions.HIDDEN;
        }

        if (this._disabledQualityControl) {
            privileges[resources.SUBMENU_QUALITY_CONTROL_SETTINGS] = permissions.HIDDEN;
            privileges[resources.CONTENT_QUALITY_CONTROL] = permissions.HIDDEN;
            privileges[resources.SETTINGS_QUALITY_CONTROL_COLOR_DIFFERENCE] = permissions.HIDDEN;
            privileges[resources.SETTINGS_QUALITY_CONTROL_DELTA_E] = permissions.HIDDEN;
            privileges[resources.SETTINGS_QUALITY_CONTROL_DELTA_L] = permissions.HIDDEN;
            privileges[resources.SETTINGS_QUALITY_CONTROL_DELTA_A] = permissions.HIDDEN;
            privileges[resources.SETTINGS_QUALITY_CONTROL_DELTA_B] = permissions.HIDDEN;
            privileges[resources.SETTINGS_QUALITY_CONTROL_METAMERISM] = permissions.HIDDEN;
            privileges[resources.SETTINGS_TABLE_QUALITY_CONTROL] = permissions.HIDDEN;
            privileges[resources.COMPONENT_QUALITY_CONTROL_MODAL] = permissions.HIDDEN;
        }

        if (this._disabledBuildingSite) {
            privileges[resources.COMPONENT_BUILDING_SITE_PANEL] = permissions.HIDDEN;
            privileges[resources.SETTINGS_TABLE_BUILDING_SITE] = permissions.HIDDEN;
        }

        if (this._activeComputerLock) {
            privileges[resources.FUNCTION_OPEN_BROWSER] = permissions.HIDDEN;
        }

        if (this._activeKiosk) {
            privileges[resources.FUNCTION_OPEN_BROWSER] = permissions.HIDDEN;
        }
    };

    /**
     * Finds and sets empty menu content sections.
     */
    applyMenuContentItemsDependencies = (privileges) => {
        if (
            privileges[resources.MACHINE_ACTION_INITIALIZE] < permissions.AVAILABLE &&
            privileges[resources.MACHINE_ACTION_RESET] < permissions.AVAILABLE &&
            privileges[resources.MACHINE_ACTION_HARD_RESET] < permissions.AVAILABLE &&
            privileges[resources.MACHINE_ACTION_ERROR_RESET] < permissions.AVAILABLE &&
            privileges[resources.MACHINE_ACTION_DISCONNECT] < permissions.AVAILABLE &&
            privileges[resources.MACHINE_ACTION_SHOW_SERVICE_WINDOW] < permissions.AVAILABLE
        ) {
            privileges[resources.CONTENT_ITEM_SINGLE_ACTIONS_GENERAL] = permissions.HIDDEN;
        }

        if (privileges[resources.COMPONENT_CANISTER_FILLING] === permissions.HIDDEN) {
            privileges[resources.CONTENT_ITEM_SINGLE_ACTIONS_CANISTERS] = permissions.HIDDEN;
        }

        if (
            privileges[resources.MACHINE_ACTION_PURGE] < permissions.AVAILABLE &&
            privileges[resources.MACHINE_ACTION_PURGE_ALL] < permissions.AVAILABLE &&
            privileges[resources.MACHINE_ACTION_SMART_PURGE] < permissions.AVAILABLE &&
            privileges[resources.MACHINE_ACTION_SMART_PURGE_ALL] < permissions.AVAILABLE
        ) {
            privileges[resources.CONTENT_ITEM_SINGLE_ACTIONS_PURGE] = permissions.HIDDEN;
        }

        if (
            privileges[resources.MACHINE_ACTION_CLEAN_CHANNELS] < permissions.AVAILABLE &&
            privileges[resources.MACHINE_ACTION_CLEAN_ALL_CHANNELS] < permissions.AVAILABLE
        ) {
            privileges[resources.CONTENT_ITEM_SINGLE_ACTIONS_CLEAN] = permissions.HIDDEN;
        }

        if (
            privileges[resources.MACHINE_ACTION_RECIRCULATE_CHANNELS] < permissions.AVAILABLE &&
            privileges[resources.MACHINE_ACTION_RECIRCULATE_ALL_CHANNELS] < permissions.AVAILABLE
        ) {
            privileges[resources.CONTENT_ITEM_SINGLE_ACTIONS_RECIRCULATE] = permissions.HIDDEN;
        }

        if (
            privileges[resources.MACHINE_ACTION_STIR_CANISTERS] < permissions.AVAILABLE &&
            privileges[resources.MACHINE_ACTION_STIR_ALL_CANISTERS] < permissions.AVAILABLE
        ) {
            privileges[resources.CONTENT_ITEM_SINGLE_ACTIONS_STIR] = permissions.HIDDEN;
        }

        if (
            privileges[resources.MACHINE_ACTION_SHELF_UP] < permissions.AVAILABLE &&
            privileges[resources.MACHINE_ACTION_SHELF_DOWN] < permissions.AVAILABLE
        ) {
            privileges[resources.CONTENT_ITEM_SINGLE_ACTIONS_SHELF] = permissions.HIDDEN;
        }

        if (
            privileges[resources.MACHINE_ACTION_CAP_OPEN] < permissions.AVAILABLE &&
            privileges[resources.MACHINE_ACTION_CAP_CLOSE] < permissions.AVAILABLE
        ) {
            privileges[resources.CONTENT_ITEM_SINGLE_ACTIONS_CAP] = permissions.HIDDEN;
        }

        if (privileges[resources.MACHINE_ACTION_PUNCH] < permissions.AVAILABLE) {
            privileges[resources.CONTENT_ITEM_SINGLE_ACTIONS_PUNCHER] = permissions.HIDDEN;
        }

        if (privileges[resources.MACHINE_ACTION_PLUG] < permissions.AVAILABLE) {
            privileges[resources.CONTENT_ITEM_SINGLE_ACTIONS_PLUGGER] = permissions.HIDDEN;
        }

        if (
            privileges[resources.FUNCTION_REBOOT_PC] < permissions.AVAILABLE &&
            privileges[resources.FUNCTION_SHUTDOWN_PC] < permissions.AVAILABLE &&
            privileges[resources.SETTINGS_DISK_SPACE_NOTIFICATION] === permissions.HIDDEN
        ) {
            privileges[resources.CONTENT_ITEM_SINGLE_ACTIONS_COMPUTER] = permissions.HIDDEN;
        }

        if (
            privileges[resources.MACHINE_SETTINGS_MACHINE_UNIT] === permissions.HIDDEN &&
            privileges[resources.MACHINE_SETTINGS_VALIDATE_COLORANTS_DURING_INIT] === permissions.HIDDEN
        ) {
            privileges[resources.CONTENT_ITEM_MACHINE_OPTIONS] = permissions.HIDDEN;
        }

        if (privileges[resources.MACHINE_SETTINGS_ENABLE_TMC] === permissions.HIDDEN) {
            privileges[resources.CONTENT_ITEM_TMC] = permissions.HIDDEN;
        }

        if (
            privileges[resources.MACHINE_SETTINGS_CHECK_CAN_BEFORE_DISPENSING] === permissions.HIDDEN &&
            privileges[resources.MACHINE_SETTINGS_SKIP_PLACE_CAN_DIALOG] === permissions.HIDDEN &&
            privileges[resources.MACHINE_SETTINGS_PLACE_CAN_SENZOR] === permissions.HIDDEN &&
            privileges[resources.MACHINE_SETTINGS_PLACE_CAN_SENZOR_DELAY] === permissions.HIDDEN &&
            privileges[resources.MACHINE_SETTINGS_SKIP_REMOVE_CAN_DIALOG] === permissions.HIDDEN &&
            privileges[resources.MACHINE_SETTINGS_REMOVE_CAN_SENZOR] === permissions.HIDDEN &&
            privileges[resources.MACHINE_SETTINGS_REMOVE_CAN_SENZOR_DELAY] === permissions.HIDDEN
        ) {
            privileges[resources.CONTENT_ITEM_MACHINE_CAN_OPTIONS] = permissions.HIDDEN;
        }

        if (
            privileges[resources.MACHINE_SETTINGS_VALIDATE_BARCODE_CAN] === permissions.HIDDEN &&
            privileges[resources.MACHINE_SETTINGS_CHECK_OVERFILL] === permissions.HIDDEN &&
            privileges[resources.MACHINE_SETTINGS_DISPENSE_INVALID_COLORANTS] === permissions.HIDDEN &&
            privileges[resources.MACHINE_SETTINGS_MINIMAL_DISPENSE_AMOUNT] === permissions.HIDDEN &&
            privileges[resources.MACHINE_SETTINGS_MANUAL_ADDITION] === permissions.HIDDEN &&
            privileges[resources.MACHINE_SETTINGS_SPLIT_FORMULA] === permissions.HIDDEN
        ) {
            privileges[resources.CONTENT_ITEM_MACHINE_DISPENSING_OPTIONS] = permissions.HIDDEN;
        }

        if (privileges[resources.MACHINE_SETTINGS_VALIDATE_BARCODE_REFILL] === permissions.HIDDEN) {
            privileges[resources.CONTENT_ITEM_MACHINE_BARCODE_REFILL_VALIDATION] = permissions.HIDDEN;
        }

        if (
            privileges[resources.SETTINGS_UFI_CODE_BASE_VALIDATION] === permissions.HIDDEN &&
            privileges[resources.SETTINGS_UFI_CODE_LOGIC] === permissions.HIDDEN &&
            privileges[resources.SETTINGS_UFI_CODE_MIN_GRAV_PERCENT] === permissions.HIDDEN &&
            privileges[resources.SETTINGS_UFI_CODE_MIN_AMOUNT_GRAV_PERCENT] === permissions.HIDDEN
        ) {
            privileges[resources.CONTENT_ITEM_MACHINE_UFI_CODE] = permissions.HIDDEN;
        }

        if (privileges[resources.SETTINGS_COLORANT_BATCH_NUMBER_LOGIC] === permissions.HIDDEN) {
            privileges[resources.CONTENT_ITEM_MACHINE_COLORANT_BATCH_NUMBERS] = permissions.HIDDEN;
        }

        if (
            privileges[resources.SETTINGS_PRINTER] === permissions.HIDDEN &&
            privileges[resources.SETTINGS_PRINT_AS_IMAGE] === permissions.HIDDEN
        ) {
            privileges[resources.CONTENT_ITEM_PRINTER_SETTINGS] = permissions.HIDDEN;
        }

        if (
            privileges[resources.SETTINGS_PRINTER_TEMPLATE] === permissions.HIDDEN &&
            privileges[resources.SETTINGS_PRINTER_TEMPLATE_QUICKTINT] === permissions.HIDDEN
        ) {
            privileges[resources.CONTENT_ITEM_TEMPLATE_SETTINGS] = permissions.HIDDEN;
        }

        if (
            privileges[resources.SETTINGS_PRINT_ON_REQUEST] === permissions.HIDDEN &&
            privileges[resources.SETTINGS_AUTOMATIC_PRINT] === permissions.HIDDEN &&
            privileges[resources.SETTINGS_PRINT_BEFORE_DISPENSING] === permissions.HIDDEN &&
            privileges[resources.SETTINGS_PRINT_AFTER_DISPENSING] === permissions.HIDDEN
        ) {
            privileges[resources.CONTENT_ITEM_PRINT_OPTIONS] = permissions.HIDDEN;
        }

        if (
            privileges[resources.COMPONENT_IMPORT_DATA] === permissions.HIDDEN &&
            privileges[resources.COMPONENT_DELETE_IMPORTED_DATA] === permissions.HIDDEN
        ) {
            privileges[resources.CONTENT_ITEM_CUSTOM_DATA] = permissions.HIDDEN;
        }

        if (
            privileges[resources.COMPONENT_IMPORT_SETTINGS] === permissions.HIDDEN &&
            privileges[resources.COMPONENT_EXPORT_LOCAL_DB] === permissions.HIDDEN &&
            privileges[resources.COMPONENT_EXPORT_SETTINGS] === permissions.HIDDEN &&
            privileges[resources.COMPONENT_EXPORT_STATIC_SQL] === permissions.HIDDEN &&
            privileges[resources.COMPONENT_RESTORE_SETTINGS] === permissions.HIDDEN
        ) {
            privileges[resources.CONTENT_ITEM_LOCAL_DATA] = permissions.HIDDEN;
        }

        if (privileges[resources.SETTINGS_FILE_TRANSFER_IN_TASKS] === permissions.HIDDEN) {
            privileges[resources.CONTENT_ITEM_FILE_TRANSFER_IMPORT] = permissions.HIDDEN;
        }

        if (privileges[resources.SETTINGS_FILE_TRANSFER_OUT_TASKS] === permissions.HIDDEN) {
            privileges[resources.CONTENT_ITEM_FILE_TRANSFER_EXPORT] = permissions.HIDDEN;
        }
    };

    /**
     * Finds and sets empty menu content sections.
     */
    applyMenuContentDependencies = (privileges) => {
        if (
            privileges[resources.CONTENT_ITEM_SINGLE_ACTIONS_GENERAL] === permissions.HIDDEN &&
            privileges[resources.CONTENT_ITEM_SINGLE_ACTIONS_CANISTERS] === permissions.HIDDEN &&
            privileges[resources.CONTENT_ITEM_SINGLE_ACTIONS_PURGE] === permissions.HIDDEN &&
            privileges[resources.CONTENT_ITEM_SINGLE_ACTIONS_CLEAN] === permissions.HIDDEN &&
            privileges[resources.CONTENT_ITEM_SINGLE_ACTIONS_RECIRCULATE] === permissions.HIDDEN &&
            privileges[resources.CONTENT_ITEM_SINGLE_ACTIONS_STIR] === permissions.HIDDEN &&
            privileges[resources.CONTENT_ITEM_SINGLE_ACTIONS_SHELF] === permissions.HIDDEN &&
            privileges[resources.CONTENT_ITEM_SINGLE_ACTIONS_CAP] === permissions.HIDDEN &&
            privileges[resources.CONTENT_ITEM_SINGLE_ACTIONS_PUNCHER] === permissions.HIDDEN &&
            privileges[resources.CONTENT_ITEM_SINGLE_ACTIONS_PLUGGER] === permissions.HIDDEN &&
            privileges[resources.CONTENT_ITEM_SINGLE_ACTIONS_COMPUTER] === permissions.HIDDEN
        ) {
            privileges[resources.CONTENT_SINGLE_ACTIONS] = permissions.HIDDEN;
        }

        if (privileges[resources.COMPONENT_SMART_PURGE_RULES] === permissions.HIDDEN) {
            privileges[resources.CONTENT_SMART_PURGE_RULES] = permissions.HIDDEN;
        }

        if (privileges[resources.COMPONENT_AUTOTASKS_SETTINGS] === permissions.HIDDEN) {
            privileges[resources.CONTENT_AUTOTASKS] = permissions.HIDDEN;
        }

        if (privileges[resources.COMPONENT_COLORANT_CONSUMPTION] === permissions.HIDDEN) {
            privileges[resources.CONTENT_COLORANT_CONSUMPTION] = permissions.HIDDEN;
        }

        if (privileges[resources.COMPONENT_COLORANT_REFILL_HISTORY] === permissions.HIDDEN) {
            privileges[resources.CONTENT_REFILL_HISTORY] = permissions.HIDDEN;
        }

        if (privileges[resources.COMPONENT_MACHINE_INFO] === permissions.HIDDEN) {
            privileges[resources.CONTENT_MACHINE_INFO] = permissions.HIDDEN;
        }

        if (
            privileges[resources.CONTENT_ITEM_MACHINE_OPTIONS] === permissions.HIDDEN &&
            privileges[resources.CONTENT_ITEM_TMC] === permissions.HIDDEN
        ) {
            privileges[resources.CONTENT_MACHINE_MANAGEMENT] = permissions.HIDDEN;
        }

        if (
            privileges[resources.CONTENT_ITEM_MACHINE_CAN_OPTIONS] === permissions.HIDDEN &&
            privileges[resources.CONTENT_ITEM_MACHINE_DISPENSING_OPTIONS] === permissions.HIDDEN
        ) {
            privileges[resources.CONTENT_DISPENSING_SETTINGS] = permissions.HIDDEN;
        }

        if (
            privileges[resources.COMPONENT_CANISTER_BARCODES] === permissions.HIDDEN &&
            privileges[resources.COMPONENT_DISPENSING_LOGIC] === permissions.HIDDEN &&
            privileges[resources.SETTINGS_TINTOMETRIC_SYSTEM] === permissions.HIDDEN
        ) {
            privileges[resources.CONTENT_CANISTER_SETTINGS] = permissions.HIDDEN;
        }

        if (privileges[resources.COMPONENT_DRIVER_SETTINGS] === permissions.HIDDEN) {
            privileges[resources.CONTENT_DRIVER_SETTINGS] = permissions.HIDDEN;
        }

        if (
            privileges[resources.CONTENT_ITEM_MACHINE_BARCODE_REFILL_VALIDATION] === permissions.HIDDEN &&
            privileges[resources.CONTENT_ITEM_MACHINE_COLORANT_BATCH_NUMBERS] === permissions.HIDDEN &&
            privileges[resources.CONTENT_ITEM_MACHINE_UFI_CODE] === permissions.HIDDEN
        ) {
            privileges[resources.CONTENT_REFILL_SETTINGS] = permissions.HIDDEN;
        }

        if (privileges[resources.COMPONENT_SPECTRO_SETTINGS] === permissions.HIDDEN) {
            privileges[resources.CONTENT_SPECTRO_SETTINGS] = permissions.HIDDEN;
        }

        if (
            privileges[resources.CONTENT_ITEM_PRINTER_SETTINGS] === permissions.HIDDEN &&
            privileges[resources.CONTENT_ITEM_TEMPLATE_SETTINGS] === permissions.HIDDEN &&
            privileges[resources.CONTENT_ITEM_PRINT_OPTIONS] === permissions.HIDDEN
        ) {
            privileges[resources.CONTENT_PRINT_SETTINGS] = permissions.HIDDEN;
        }

        if (privileges[resources.COMPONENT_RCC_CONNECTION] === permissions.HIDDEN) {
            privileges[resources.CONTENT_RCC_CONNECTION] = permissions.HIDDEN;
        }

        if (
            privileges[resources.COMPONENT_DATABASE_MODAL] === permissions.HIDDEN &&
            privileges[resources.COMPONENT_BARCODE_TEST_MODAL] === permissions.HIDDEN
        ) {
            privileges[resources.CONTENT_DATABASE_CONNECTION] = permissions.HIDDEN;
        }

        if (
            privileges[resources.COMPONENT_CLIENT_IDENTIFIER] === permissions.HIDDEN &&
            privileges[resources.COMPONENT_ACTIVE_SERVER] === permissions.HIDDEN &&
            privileges[resources.COMPONENT_CLIENT_SERVER_CONNECTION] === permissions.HIDDEN
        ) {
            privileges[resources.CONTENT_SERVER_CONNECTION] = permissions.HIDDEN;
        }

        if (
            privileges[resources.CONTENT_ITEM_CUSTOM_DATA] === permissions.HIDDEN &&
            privileges[resources.CONTENT_ITEM_LOCAL_DATA] === permissions.HIDDEN
        ) {
            privileges[resources.CONTENT_IMPORT_CUSTOM_DATA] = permissions.HIDDEN;
        }

        if (
            privileges[resources.CONTENT_ITEM_FILE_TRANSFER_IMPORT] === permissions.HIDDEN &&
            privileges[resources.CONTENT_ITEM_FILE_TRANSFER_EXPORT] === permissions.HIDDEN &&
            privileges[resources.CONTENT_ITEM_ONE_TIME_IMPORT] === permissions.HIDDEN
        ) {
            privileges[resources.CONTENT_FILE_TRANSFER] = permissions.HIDDEN;
        }

        if (privileges[resources.COMPONENT_SERVER_PLUGINS] === permissions.HIDDEN) {
            privileges[resources.CONTENT_SERVER_PLUGINS] = permissions.HIDDEN;
        }

        if (privileges[resources.COMPONENT_REGISTRATION] === permissions.HIDDEN) {
            privileges[resources.COMPONENT_MACHINE_REGISTRATION] = permissions.HIDDEN;
            privileges[resources.CONTENT_REGISTRATION] = permissions.HIDDEN;
        }

        if (privileges[resources.COMPONENT_POS_INFO] === permissions.HIDDEN) {
            privileges[resources.CONTENT_POS_INFO] = permissions.HIDDEN;
        }

        if (
            privileges[resources.COMPONENT_LICENSE_IDENTIFICATION] === permissions.HIDDEN &&
            privileges[resources.COMPONENT_LICENSE_STATUS] === permissions.HIDDEN &&
            privileges[resources.COMPONENT_LICENSE_SUPPLIER] === permissions.HIDDEN
        ) {
            privileges[resources.CONTENT_LICENSE_STATUS] = permissions.HIDDEN;
        }

        if (privileges[resources.COMPONENT_UPDATE_OPTIONS] === permissions.HIDDEN) {
            privileges[resources.CONTENT_UPDATE_OPTIONS] = permissions.HIDDEN;
        }

        if (privileges[resources.COMPONENT_BUG_REPORT] === permissions.HIDDEN) {
            privileges[resources.CONTENT_BUG_REPORT] = permissions.HIDDEN;
        }
    };

    /**
     * Finds and sets empty submenu sections.
     */
    applySubmenuDependencies = (privileges) => {
        if (privileges[resources.CONTENT_SINGLE_ACTIONS] === permissions.HIDDEN) {
            privileges[resources.SUBMENU_SINGLE_ACTIONS] = permissions.HIDDEN;
        }

        if (privileges[resources.CONTENT_SMART_PURGE_RULES] === permissions.HIDDEN) {
            privileges[resources.SUBMENU_SMART_PURGE_RULES] = permissions.HIDDEN;
        }

        if (privileges[resources.CONTENT_AUTOTASKS] === permissions.HIDDEN) {
            privileges[resources.SUBMENU_AUTOTASKS] = permissions.HIDDEN;
        }

        if (privileges[resources.CONTENT_COLORANT_CONSUMPTION] === permissions.HIDDEN) {
            privileges[resources.SUBMENU_COLORANT_CONSUMPTION] = permissions.HIDDEN;
        }

        if (privileges[resources.CONTENT_REFILL_HISTORY] === permissions.HIDDEN) {
            privileges[resources.SUBMENU_REFILL_HISTORY] = permissions.HIDDEN;
        }

        if (privileges[resources.CONTENT_MACHINE_INFO] === permissions.HIDDEN) {
            privileges[resources.SUBMENU_MACHINE_INFO] = permissions.HIDDEN;
        }

        if (privileges[resources.CONTENT_MACHINE_MANAGEMENT] === permissions.HIDDEN) {
            privileges[resources.SUBMENU_MACHINE_MANAGEMENT] = permissions.HIDDEN;
        }

        if (privileges[resources.CONTENT_DISPENSING_SETTINGS] === permissions.HIDDEN) {
            privileges[resources.SUBMENU_DISPENSING_SETTINGS] = permissions.HIDDEN;
        }

        if (privileges[resources.CONTENT_CANISTER_SETTINGS] === permissions.HIDDEN) {
            privileges[resources.SUBMENU_CANISTER_SETTINGS] = permissions.HIDDEN;
        }

        if (privileges[resources.CONTENT_DRIVER_SETTINGS] === permissions.HIDDEN) {
            privileges[resources.SUBMENU_DRIVER_SETTINGS] = permissions.HIDDEN;
        }

        if (privileges[resources.CONTENT_REFILL_SETTINGS] === permissions.HIDDEN) {
            privileges[resources.SUBMENU_REFILL_SETTINGS] = permissions.HIDDEN;
        }

        if (privileges[resources.CONTENT_REGISTRATION] === permissions.HIDDEN) {
            privileges[resources.SUBMENU_REGISTRATION] = permissions.HIDDEN;
        }

        if (privileges[resources.CONTENT_POS_INFO] === permissions.HIDDEN) {
            privileges[resources.SUBMENU_POS_INFO] = permissions.HIDDEN;
        }

        if (privileges[resources.CONTENT_LICENSE_STATUS] === permissions.HIDDEN) {
            privileges[resources.SUBMENU_LICENSE_STATUS] = permissions.HIDDEN;
        }
    };

    /**
     * Finds and sets empty main menu sections.
     */
    applyMainMenuDependencies = (privileges) => {
        if (
            privileges[resources.SUBMENU_SINGLE_ACTIONS] === permissions.HIDDEN &&
            privileges[resources.SUBMENU_SMART_PURGE_RULES] === permissions.HIDDEN &&
            privileges[resources.SUBMENU_AUTOTASKS] === permissions.HIDDEN
        ) {
            privileges[resources.MENU_SERVICE_ACTIONS] = permissions.HIDDEN;
        }

        if (
            privileges[resources.SUBMENU_COLORANT_CONSUMPTION] === permissions.HIDDEN &&
            privileges[resources.SUBMENU_REFILL_HISTORY] === permissions.HIDDEN
        ) {
            privileges[resources.MENU_STATISTICS] = permissions.HIDDEN;
        }

        if (
            privileges[resources.SUBMENU_MACHINE_INFO] === permissions.HIDDEN &&
            privileges[resources.SUBMENU_MACHINE_MANAGEMENT] === permissions.HIDDEN
        ) {
            privileges[resources.MENU_MACHINE_MANAGEMENT] = permissions.HIDDEN;
        }

        if (
            privileges[resources.SUBMENU_DISPENSING_SETTINGS] === permissions.HIDDEN &&
            privileges[resources.SUBMENU_CANISTER_SETTINGS] === permissions.HIDDEN &&
            privileges[resources.SUBMENU_DRIVER_SETTINGS] === permissions.HIDDEN &&
            privileges[resources.SUBMENU_REFILL_SETTINGS] === permissions.HIDDEN
        ) {
            privileges[resources.MENU_MACHINE_SETTINGS] = permissions.HIDDEN;
        }

        if (privileges[resources.CONTENT_SPECTRO_SETTINGS] === permissions.HIDDEN) {
            privileges[resources.MENU_SPECTRO_SETTINGS] = permissions.HIDDEN;
        }

        if (privileges[resources.CONTENT_PRINT_SETTINGS] === permissions.HIDDEN) {
            privileges[resources.MENU_PRINT_SETTINGS] = permissions.HIDDEN;
        }

        if (privileges[resources.CONTENT_RCC_CONNECTION] === permissions.HIDDEN) {
            privileges[resources.MENU_RCC_CONNECTION] = permissions.HIDDEN;
        }

        if (privileges[resources.CONTENT_DATABASE_CONNECTION] === permissions.HIDDEN) {
            privileges[resources.MENU_DATABASE_CONNECTION] = permissions.HIDDEN;
        }

        if (privileges[resources.CONTENT_SERVER_CONNECTION] === permissions.HIDDEN) {
            privileges[resources.MENU_SERVER_CONNECTION] = permissions.HIDDEN;
        }

        if (privileges[resources.CONTENT_IMPORT_CUSTOM_DATA] === permissions.HIDDEN) {
            privileges[resources.MENU_IMPORT_CUSTOM_DATA] = permissions.HIDDEN;
        }

        if (privileges[resources.CONTENT_FILE_TRANSFER] === permissions.HIDDEN) {
            privileges[resources.MENU_FILE_TRANSFER] = permissions.HIDDEN;
        }

        if (privileges[resources.CONTENT_SERVER_PLUGINS] === permissions.HIDDEN) {
            privileges[resources.MENU_SERVER_PLUGINS] = permissions.HIDDEN;
        }

        if (
            privileges[resources.SUBMENU_REGISTRATION] === permissions.HIDDEN &&
            privileges[resources.SUBMENU_POS_INFO] === permissions.HIDDEN &&
            privileges[resources.SUBMENU_LICENSE_STATUS] === permissions.HIDDEN
        ) {
            privileges[resources.MENU_LICENSE_DATA] = permissions.HIDDEN;
        }

        if (privileges[resources.CONTENT_UPDATE_OPTIONS] === permissions.HIDDEN) {
            privileges[resources.MENU_UPDATE] = permissions.HIDDEN;
        }

        if (privileges[resources.CONTENT_BUG_REPORT] === permissions.HIDDEN) {
            privileges[resources.MENU_BUG_REPORT] = permissions.HIDDEN;
        }
    };

    /**
     * Finds and sets empty main menu sections.
     */
    applyMainMenuSectionDependencies = (privileges) => {
        if (
            privileges[resources.FUNCTION_SWITCH_TO_LIGHT] < permissions.AVAILABLE &&
            privileges[resources.FUNCTION_SWITCH_TO_INTERGAMMA] < permissions.AVAILABLE &&
            privileges[resources.FUNCTION_FIND_IN_SETTINGS] < permissions.AVAILABLE
        ) {
            privileges[resources.MENU_SIMPLIFIERS] = permissions.HIDDEN;
        }

        if (
            privileges[resources.MENU_SERVICE_ACTIONS] === permissions.HIDDEN &&
            privileges[resources.MENU_STATISTICS] === permissions.HIDDEN
        ) {
            privileges[resources.MENU_MAINTENANCE] = permissions.HIDDEN;
        }

        if (
            privileges[resources.MENU_MACHINE_MANAGEMENT] === permissions.HIDDEN &&
            privileges[resources.MENU_MACHINE_SETTINGS] === permissions.HIDDEN
        ) {
            privileges[resources.MENU_MACHINE_AND_DEVICES] = permissions.HIDDEN;
        }

        if (
            privileges[resources.MENU_SPECTRO_SETTINGS] === permissions.HIDDEN &&
            privileges[resources.MENU_PRINT_SETTINGS] === permissions.HIDDEN &&
            privileges[resources.MENU_RCC_CONNECTION] === permissions.HIDDEN
        ) {
            privileges[resources.MENU_DEVICES] = permissions.HIDDEN;
        }

        if (privileges[resources.MENU_MACHINE_AND_DEVICES] !== permissions.HIDDEN) {
            privileges[resources.MENU_DEVICES] = permissions.HIDDEN;
        }

        if (!this._isSettingsMenuVisible) {
            privileges[resources.MENU_SETTINGS] = permissions.HIDDEN;
        }

        if (
            privileges[resources.MENU_DATABASE_CONNECTION] === permissions.HIDDEN &&
            privileges[resources.MENU_SERVER_CONNECTION] === permissions.HIDDEN &&
            privileges[resources.MENU_IMPORT_CUSTOM_DATA] === permissions.HIDDEN &&
            privileges[resources.MENU_FILE_TRANSFER] === permissions.HIDDEN &&
            privileges[resources.MENU_SERVER_PLUGINS] === permissions.HIDDEN
        ) {
            privileges[resources.MENU_DATABASE] = permissions.HIDDEN;
        }

        if (
            privileges[resources.MENU_LICENSE_DATA] === permissions.HIDDEN &&
            privileges[resources.MENU_UPDATE] === permissions.HIDDEN &&
            privileges[resources.MENU_BUG_REPORT] === permissions.HIDDEN
        ) {
            privileges[resources.MENU_LICENSE] = permissions.HIDDEN;
        }

        if (
            privileges[resources.MENU_SIMPLIFIERS] === permissions.HIDDEN &&
            privileges[resources.MENU_MAINTENANCE] === permissions.HIDDEN &&
            privileges[resources.MENU_MACHINE_AND_DEVICES] === permissions.HIDDEN &&
            privileges[resources.MENU_DEVICES] === permissions.HIDDEN &&
            privileges[resources.MENU_SETTINGS] === permissions.HIDDEN &&
            privileges[resources.MENU_DATABASE] === permissions.HIDDEN &&
            privileges[resources.MENU_LICENSE] === permissions.HIDDEN
        ) {
            privileges[resources.MENU] = permissions.HIDDEN;
        }
    };

    /**
     * Finds and sets empty menu sections.
     */
    applyMenuDependencies = (privileges) => {
        this.applyMenuContentItemsDependencies(privileges);
        this.applyMenuContentDependencies(privileges);
        this.applySubmenuDependencies(privileges);
        this.applyMainMenuDependencies(privileges);
        this.applyMainMenuSectionDependencies(privileges);
    };

    /**
     * Sets privileges of setup subsections.
     */
    applySetupSubcontentDependencies = (privileges) => {
        if (privileges[resources.COMPONENT_DRIVER_SETTINGS] === permissions.HIDDEN) {
            privileges[resources.SETUP_MACHINE_DRIVER] = permissions.HIDDEN;
        }

        if (
            privileges[resources.CONTENT_MACHINE_MANAGEMENT] === permissions.HIDDEN &&
            privileges[resources.CONTENT_DISPENSING_SETTINGS] === permissions.HIDDEN &&
            privileges[resources.SETTINGS_TINTOMETRIC_SYSTEM] === permissions.HIDDEN
        ) {
            privileges[resources.SETUP_MACHINE_SETTINGS] = permissions.HIDDEN;
        }

        if (privileges[resources.CONTENT_ITEM_PRINTER_SETTINGS] === permissions.HIDDEN) {
            privileges[resources.SETUP_PRINTER_SETTINGS] = permissions.HIDDEN;
        }

        if (privileges[resources.CONTENT_ITEM_TEMPLATE_SETTINGS] === permissions.HIDDEN) {
            privileges[resources.SETUP_PRINTER_TEMPLATE_SETTINGS] = permissions.HIDDEN;
        }

        if (privileges[resources.CONTENT_ITEM_PRINT_OPTIONS] === permissions.HIDDEN) {
            privileges[resources.SETUP_PRINTER_OPTIONS] = permissions.HIDDEN;
        }

        if (
            privileges[resources.SETTINGS_KIOSK] === permissions.HIDDEN &&
            privileges[resources.SETTINGS_LIGHT_ENABLED] === permissions.HIDDEN &&
            privileges[resources.SETTINGS_ORDERING_ENABLED] === permissions.HIDDEN
        ) {
            privileges[resources.SETUP_ENVIRONMENT_LAYOUT] = permissions.HIDDEN;
        }

        if (privileges[resources.SETTINGS_NAVIGATION_ITEMS_ORDERED] === permissions.HIDDEN) {
            privileges[resources.SETUP_ENVIRONMENT_NAVIGATION] = permissions.HIDDEN;
        }

        if (
            privileges[resources.SETTINGS_FORMULA_PREVIEW] === permissions.HIDDEN &&
            privileges[resources.SETTINGS_FORMULA_CODE] === permissions.HIDDEN &&
            privileges[resources.SETTINGS_EDIT_COLORANT_ALLOWANCE] === permissions.HIDDEN &&
            privileges[resources.SETTINGS_FORMULA_UNITS] === permissions.HIDDEN
        ) {
            privileges[resources.SETUP_ENVIRONMENT_FORMULA] = permissions.HIDDEN;
        }

        if (
            privileges[resources.SETTINGS_CUSTOM_FORMULAS] === permissions.HIDDEN &&
            privileges[resources.SETTINGS_CUSTOM_CAN_SIZE] === permissions.HIDDEN &&
            privileges[resources.SETTINGS_INFO_TYPES] === permissions.HIDDEN &&
            privileges[resources.SETTINGS_PRICE_TYPES] === permissions.HIDDEN &&
            privileges[resources.SETTINGS_PRICE_VISIBILITY_TYPES] === permissions.HIDDEN
        ) {
            privileges[resources.SETUP_ENVIRONMENT_FORMULA_DATA] = permissions.HIDDEN;
        }

        if (privileges[resources.COMPONENT_UPDATE_OPTIONS] === permissions.HIDDEN) {
            privileges[resources.SETUP_ENVIRONMENT_UPDATE] = permissions.HIDDEN;
        }

        if (privileges[resources.CONTENT_ITEM_CUSTOM_DATA] === permissions.HIDDEN) {
            privileges[resources.SETUP_ENVIRONMENT_IMPORT] = permissions.HIDDEN;
        }
    };

    /**
     * Sets privileges of main setup sections.
     */
    applySetupContentDependencies = (privileges) => {
        if (
            privileges[resources.SETUP_MACHINE_DRIVER] === permissions.HIDDEN &&
            privileges[resources.SETUP_MACHINE_SETTINGS] === permissions.HIDDEN
        ) {
            privileges[resources.SETUP_MACHINE] = permissions.HIDDEN;
        }

        if (
            privileges[resources.SETUP_PRINTER_SETTINGS] === permissions.HIDDEN &&
            privileges[resources.SETUP_PRINTER_TEMPLATE_SETTINGS] === permissions.HIDDEN &&
            privileges[resources.SETUP_PRINTER_OPTIONS] === permissions.HIDDEN
        ) {
            privileges[resources.SETUP_PRINTER] = permissions.HIDDEN;
        }

        if (privileges[resources.COMPONENT_SPECTRO_SETTINGS] === permissions.HIDDEN) {
            privileges[resources.SETUP_SPECTRO] = permissions.HIDDEN;
        }

        if (
            privileges[resources.SETUP_ENVIRONMENT_LAYOUT] === permissions.HIDDEN &&
            privileges[resources.SETUP_ENVIRONMENT_NAVIGATION] === permissions.HIDDEN &&
            privileges[resources.SETUP_ENVIRONMENT_FORMULA] === permissions.HIDDEN &&
            privileges[resources.SETUP_ENVIRONMENT_FORMULA_DATA] === permissions.HIDDEN &&
            privileges[resources.SETUP_ENVIRONMENT_UPDATE] === permissions.HIDDEN &&
            privileges[resources.SETUP_ENVIRONMENT_IMPORT] === permissions.HIDDEN
        ) {
            privileges[resources.SETUP_ENVIRONMENT] = permissions.HIDDEN;
        }
    };

    /**
     * Sets privileges of setup sections.
     */
    applySetupDependencies = (privileges) => {
        this.applySetupSubcontentDependencies(privileges);
        this.applySetupContentDependencies(privileges);
    };

    /**
     * @return  {Object}
     */
    buildPrivileges = () => {
        let result = modelHelper.copy(this._privileges);

        this.applyProductDependencies(result);
        this.applyModuleDependencies(result);
        this.applyInitialSoftwareTypeDependencies(result);
        this.applySoftwareTypeDependencies(result);
        this.applyPlatformDependencies(result);
        this.applyMachineDependencies(result);
        this.applyMachineDevicesDependencies(result);
        this.applySpectroDependencies(result);
        this.applyCustomDependencies(result);
        this.applyMenuDependencies(result);
        this.applySetupDependencies(result);

        return result;
    };
}
